import style from "@/styles/pages/Employment/Employment.module.css";
import { FormProvider, useForm } from "react-hook-form";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { RootState } from "@/store/dtm";
import CurrencyInput from "react-currency-input-field";
import { setApplicationProgress } from "@/slices/applicationSlice";
import SlideTransition from "@/styles/animation/SlideTransition";
import { createApiClient } from "@/lib/Requests";
import { ApplicationDto, EmploymentDto, EmploymentIncomeDto, EmploymentModel, OtherIncome } from "@/lib/DTMWebAPIClient.generated";
import { selectPropertyInfo } from "@/slices/propertyInfoSlice";
import SearchableDropdown from "@/components/shared/SearchableDropdown";
import InputMask from "react-input-mask";
import { AnimatePresence, motion } from "framer-motion";
import ManualAddress from "@/components/Address/ManualAddress";
import { addressInfo } from "@/slices/PreviousAddressSlice";
import { streetDirections } from "@/ts/streetDirections";
import { streetTypes } from "@/ts/streetTypes";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { selectEmployment } from "@/slices/employmentSlice";
import { addCurrentEmployment, clearCurrentEmploymentState, currentEmploymentInfo, employerAddressInfo, removeCurrentEmployment, selectCurrentEmployment, setCurrentEmploymentInfo } from "@/slices/currentEmploymentSlice";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { applicationCategory } from "@/types/applicationType";
import { setClickInsightApplicationProgress, setClickInsightEvents, setClickInsightManualFormErrors } from "@/lib/util";
import { setCachedAssets } from "@/slices/sessionDataSlice";

export default function Employment() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { state } = useLocation();

    const [editMode, setEditMode] = useState(false);
    const [manualAddressShown, setmanualAddressShown] = useState(false);
    const [currentAddressInfo, setCurrentAddressInfo] = useState<addressInfo>({
        id: 0,
        displayId: "",
        streetNumber: "",
        streetName: "",
        streetType: "",
        streetDirection: "",
        city: "",
        province: "",
        country: "",
        postalCode: "",
        unitType: "",
        unitNumber: "",
        manualAddressEntered: true,
        yearsLivedOn: 0,
        monthsLivedOn: 0
    });
    const [addressSelected, setAddressSelected] = useState(false);
    const [addressSelectionError, setAddressSelectionError] = useState(false);
    const [focusErrorType, setFocusErrorType] = useState(0);
    const [highlightError, setHighlightError] = useState(0);

    const [isSaving, setIsSaving] = useState(false);
    const [displayError, setDisplayError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    const employment = useSelector(selectEmployment).employment;
    const currentEmployment = useSelector(selectCurrentEmployment).employments;
    const [empErrors, setEmpErrors] = useState<Record<string, string>[]>([]);
    const [empAddrErrors, setEmpAddrErrors] = useState<string[][]>([]);
    const sessionData = useSelector((state: RootState) => state.sessionData.currentSession);
    const [fetchingCachedData, setFetchingCachedData] = useState(false);
    const [retirementAvailable, setRetirementAvailable] = useState(true);
    const [unemployedAvailable, setUnemployedAvailable] = useState(true);
    const propertyInfo = useSelector(selectPropertyInfo).propertyInfo;
    const applicant = useSelector(
        (state: RootState) => state.applicant.applicant
    );
    const application = useSelector(
        (state: RootState) => state.application.application
    );
    const credential = useSelector(
        (state: RootState) => state.credential
    );
    const assets = useSelector((state: RootState) => state.assets.assets);
    const referralProducts = useSelector((state: RootState) => state.referralProducts.products);
    const client = createApiClient({ timeout: 60000, token: credential.credential.accessToken });

    const methods = useForm({
        defaultValues: {
            employmentStatus: employment.status,
            employmentType: employment.type,
            payrollType: "",
            income: "",
            payrollFrequency: "",
            occupation: "",
            jobTitle: "",
            ownership: "",
            businessType: "",
            industryYears: "",
            industryMonths: "",
            employmentYears: "",
            employmentMonths: "",
            unemployedYears: "",
            unemployedMonths: "",
            retiredYears: "",
            retiredMonths: "",

            businessLendingFromMCU: "",
            overtimeIncome: "",
            overtimeIncomeFrequency: "",
            bonusIncome: "",
            bonusIncomeFrequency: "",
            commissionIncome: "",
            commissionIncomeFrequency: "",
            tipsIncome: "",
            tipsIncomeFrequency: "",

            employerName: "",
            employerPhoneNumber: "",
            autoAddress: "",
            streetNumber: "",
            streetName: "",
            streetType: "",
            streetDirection: "",
            unitType: "",
            unitNumber: "",
            cityName: "",
            provinceName: "",
            postalCode: "",
            country: "",

            probation: "",
            businessFamilyOwned: "",
            leave: "",
            leaveType: "",
            otherLeaveDescription: "",
            workReturnDate: ""
        },
        shouldFocusError: true,
    });

    const options = {
        key: "XD95-YY77-BP99-EM55",
    };

    useEffect(() => {
        document.title = "Enter Employment Details";
        setClickInsightEvents(referralProducts, undefined, undefined, undefined, undefined, application.id, applicant);

        dispatch(setCachedAssets(assets));
        client.apiApplicationsFetch().then((result) => {
            result.application = application;
            result.propertyInfo = propertyInfo;
            result.assets = assets;
            client.apiApplicationsSave(new ApplicationDto({
                applicationData: JSON.stringify(result)
            })).catch((error) => {
                console.log("Error saving application info");
            });
        }).catch((error) => {
            console.log("Error fetching application info", error);
        });

        if (state != null) {
            setEditMode(true);
        }

        //Fetch Cached Employment Data (if Exists)
        if (sessionData.cachedCurrentEmployment && sessionData.cachedCurrentEmployment.length > 0) {
            dispatch(clearCurrentEmploymentState());
            sessionData.cachedCurrentEmployment.forEach((employment) => {
                dispatch(addCurrentEmployment(employment));
            });
        }

    }, []);

    useEffect(() => {
        let newEmpErrors: Record<string, string>[] = [];
        setRetirementAvailable(true);
        setUnemployedAvailable(true);
        let outstandingErrors = false;
        currentEmployment.forEach((emp: currentEmploymentInfo, index: any) => {
            let id = index + 1;

            //Only render Canada Post Autocomplete if visible and status is set (to improve rendering performance)
            if (emp.visible && (emp.status === "employed" || emp.status === "selfemployed")) {
                let fields = [
                    {
                        element: "empAddressSearch" + id,
                        field: "",
                        mode: window.pca?.fieldMode.SEARCH,
                    },
                    {
                        element: "span-street-address-" + id,
                        field: "Line1",
                        mode: window.pca?.fieldMode.POPULATE,
                    },
                    {
                        element: "span-street-address2-" + id,
                        field: "Line2",
                        mode: window.pca?.fieldMode?.POPULATE,
                    },
                    { element: "city-" + id, field: "City", mode: window.pca?.fieldMode.POPULATE },
                    {
                        element: "span-state-" + id,
                        field: "ProvinceName",
                        mode: window.pca?.fieldMode.POPULATE,
                    },
                    {
                        element: "",
                        field: "PostalCode",
                        mode: window.pca?.fieldMode.POPULATE,
                    },
                    {
                        element: "span-country-" + id,
                        field: "CountryName",
                        mode: window.pca?.fieldMode.POPULATE,
                    },
                ];
                let control = new window.pca.Address(fields, options);
                control.listen("populate", (address: any) => {
                    let streetInfo = address.Line1.split(" ");
                    let streetName = "";
                    let streetDirection = "";
                    let streetType = "";
                    let streetComponents: string[] = address.Street.split(" ");
                    let lastStreetComponent = streetComponents[streetComponents.length - 1].toUpperCase();

                    //Check if street direction and type exists in the street name
                    if (streetDirections.includes(lastStreetComponent)) {
                        streetDirection = lastStreetComponent;
                        let secondLastStreetComponent = streetComponents[streetComponents.length - 2].toUpperCase();
                        if (streetTypes.includes(secondLastStreetComponent)) {
                            streetType = secondLastStreetComponent;
                        }
                        streetComponents.forEach((value, index) => {
                            if (index < streetComponents.length - 2) {
                                streetName += value + " "
                            }
                        });
                    } else if (streetTypes.includes(lastStreetComponent)) {
                        streetType = lastStreetComponent;
                        streetComponents.forEach((value, index) => {
                            if (index != streetComponents.length - 1) {
                                streetName += value + " "
                            }
                        });
                    } else {
                        streetComponents.forEach((value, index) => {
                            console.log(value, index);
                            streetName += value + " "
                        });
                    }
                    streetName = streetName.trim();

                    //Check if unit number is embeded in the street number
                    let streetNumber = "";
                    let streetNumberComponents = streetInfo[0].split("-");
                    if (streetNumberComponents.length > 1) {
                        streetNumber = streetNumberComponents[streetNumberComponents.length - 1];
                    } else {
                        streetNumber = streetInfo[0];
                    }

                    let newEmploymentInfo: currentEmploymentInfo = { ...emp }
                    newEmploymentInfo.employerAddress = {
                        displayId: emp.employerAddress.displayId,
                        id: emp.employerAddress.id,
                        streetNumber: streetNumber,
                        streetName: streetName,
                        streetType: streetType,
                        streetDirection: streetDirection,
                        city: address.City,
                        province: address.ProvinceCode,
                        country: address.CountryName,
                        postalCode: address.PostalCode,
                        unitType: address.SubBuilding != undefined && address.SubBuilding != "" ? "unit" : "",
                        unitNumber: address.SubBuilding != undefined ? address.SubBuilding : ""
                    }
                    //If Street Type is not in the master list, toggle manual address entry
                    if (!streetTypes.includes(newEmploymentInfo.employerAddress.streetType)) {
                        newEmploymentInfo.manualAddressEntered = true;
                    }
                    dispatch(setCurrentEmploymentInfo(newEmploymentInfo));

                    if (empErrors[parseInt(id) - 1]["autoAddress"] != undefined) {
                        let newEmpErrors = [...empErrors];
                        delete newEmpErrors[parseInt(id) - 1]["autoAddress"];
                        delete newEmpErrors[parseInt(id) - 1]["streetNumber"];
                        delete newEmpErrors[parseInt(id) - 1]["streetName"];
                        delete newEmpErrors[parseInt(id) - 1]["streetType"];
                        delete newEmpErrors[parseInt(id) - 1]["streetDirection"];
                        delete newEmpErrors[parseInt(id) - 1]["city"];
                        delete newEmpErrors[parseInt(id) - 1]["province"];
                        delete newEmpErrors[parseInt(id) - 1]["postalCode"];
                        delete newEmpErrors[parseInt(id) - 1]["country"];
                        setEmpErrors(newEmpErrors);
                    }
                });
            }
            let errors: Record<string, string> = empErrors[index] ? empErrors[index] : {};
            newEmpErrors.push(errors);

            if (Object.keys(errors).length > 0) {
                outstandingErrors = true;
            }

            if (emp.status.trim() === "retired") {
                setRetirementAvailable(false);

                if (emp.otherIncome == undefined || emp.otherIncome.length == 0) {
                    let newEmploymentInfo: currentEmploymentInfo = { ...emp }
                    newEmploymentInfo.noRetirementIncome = false;
                    newEmploymentInfo.otherIncome = [{
                        id: 0,
                        displayId: "1",
                        type: "",
                        amount: 0,
                        amountText: "",
                        frequency: ""
                    }]
                    dispatch(setCurrentEmploymentInfo(newEmploymentInfo));
                }
            }
            if (emp.status.trim() === "unemployed") {
                setUnemployedAvailable(false);
            }
        });
        setEmpErrors(newEmpErrors);
        setDisplayError(outstandingErrors);

    }, [currentEmployment])

    useEffect(() => {
        if (!manualAddressShown) {
            methods.unregister([
                "streetNumber",
                "streetName",
                "unitNumber",
                "cityName",
                "provinceName",
                "postalCode",
                "country",
            ]);
            if (currentAddressInfo && currentAddressInfo.streetName.trim() == "") {
            }
        } else {
            //methods.setValue("autoAddress", "");
            if (addressSelected && currentAddressInfo) {
                methods.setValue("streetNumber", currentAddressInfo.streetNumber);
                methods.setValue("streetName", currentAddressInfo.streetName);
                methods.setValue("streetType", currentAddressInfo.streetType);
                methods.setValue("streetDirection", currentAddressInfo.streetDirection);
                methods.setValue("cityName", currentAddressInfo.city);
                methods.setValue("provinceName", currentAddressInfo.province);
                methods.setValue("postalCode", currentAddressInfo.postalCode);
                methods.setValue("country", currentAddressInfo.country);
            } else {
                methods.setValue("streetNumber", "");
                methods.setValue("streetName", "");
                methods.setValue("streetType", "");
                methods.setValue("streetDirection", "");
                methods.setValue("cityName", "");
                methods.setValue("provinceName", "");
                methods.setValue("postalCode", "");
                methods.setValue("country", "");
            }

            methods.unregister("autoAddress");
            methods.register("streetNumber", {
                required: "Enter your street number",
                pattern: {
                    value: /^[ #a-zA-Z0-9._-]+$/,
                    message: " Please enter a valid street number",
                },
            });
            methods.register("unitNumber", {
                required: methods.getValues("unitType").trim() != "" ? "Enter your unit number" : false,
                pattern: {
                    value: /^[ #a-zA-Z0-9._-]+$/,
                    message:
                        " Please enter a valid unit number. Only letters, numbers and _-.# are accepted",
                },
            });
            methods.register("streetName", {
                required: "Enter your street name",
                pattern: {
                    value: /^[ #a-zA-ZÀ-Ÿ0-9._-]+$/,
                    message: " Please enter a valid street name",
                },
            });
            methods.register("streetType", {
                required: "Select your street type"
            });
            methods.register("cityName", {
                required: "Enter your city name",
                pattern: {
                    value: /^[ #a-zA-ZÀ-Ÿ0-9._-]+$/,
                    message: " Please enter a valid city name",
                },
            });
            methods.register("provinceName", {
                required: "Select your province name",
            });
            methods.register("postalCode", {
                required: "Enter your postal code",
            });
            methods.register("country", {
                required: "Select your country name",
            });
        }

        methods.setFocus("occupation")
    }, [manualAddressShown, methods]);

    useEffect(() => {
        if (currentAddressInfo.streetNumber.trim() != "" && !manualAddressShown) {
            setAddressSelected(true);
            setAddressSelectionError(false);
        }

    }, [currentAddressInfo]);

    useEffect(() => {
        let employerErrors: string[][] = [];
        for (let i = 0; i < empErrors.length; i++) {
            const errorObj = empErrors[i];
            let currentEmp = [];
            for (const key in errorObj) {
                currentEmp.push(key);
            }
            employerErrors.push(currentEmp);
        }
        setEmpAddrErrors(employerErrors);
    }, [empErrors]);

    const onError = (errors: object) => {
        console.log(errors);
        setHighlightError(0);
    }

    const onSubmit = async (data: any) => {
        let emptyFieldExists = false;
        let employmentErrors: Record<string, string>[] = [];

        let retiredDeclared: boolean = false;
        let unemployedDeclared: boolean = false;

        currentEmployment.forEach((c, index) => {
            let errors: Record<string, string> = {};
            if (c.status.trim() == "") {
                emptyFieldExists = true;
                errors["employmentStatus"] = "Select an option.";
            }

            if (c.status.trim() == "employed" || c.status.trim() == "selfemployed") {
                if (c.type.trim() == "") {
                    emptyFieldExists = true;
                    errors["employmentType"] = "Select an option.";
                }
                if (c.occupation == undefined || c.occupation.trim() == "") {
                    emptyFieldExists = true;
                    errors["occupation"] = "Select an occupation.";
                }
                if (c.jobTitle.trim() == "") {
                    emptyFieldExists = true;
                    errors["jobTitle"] = "Enter a job title.";
                }
                if (c.industryYears == undefined) {
                    emptyFieldExists = true;
                    errors["industryLengthYears"] = "Enter the number of years.";
                }
                if (c.industryMonths == undefined) {
                    emptyFieldExists = true;
                    errors["industryLengthMonths"] = "Enter the number of months.";
                }
                if (c.employmentYears == undefined) {
                    emptyFieldExists = true;
                    errors["employmentYears"] = "Enter the number of years.";
                }
                if (c.employmentMonths == undefined) {
                    emptyFieldExists = true;
                    errors["employmentMonths"] = "Enter the number of months.";
                }
                if (c.employerName.trim() == "") {
                    emptyFieldExists = true;
                    errors["employerName"] = "Enter your employer name.";
                }
                if (c.employerPhone.trim() == "") {
                    emptyFieldExists = true;
                    errors["employerPhoneNumber"] = "Enter your employer phone number.";
                } else {
                    if (!/^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/.test(c.employerPhone.trim())) {
                        emptyFieldExists = true;
                        errors["employerPhoneNumber"] = "Enter a valid phone number.";
                    }
                }

                if (c.status.trim() == "selfemployed" && c.ownershipType.trim() == "") {
                    emptyFieldExists = true;
                    errors["ownership"] = "Select an option.";
                }
                if (c.status.trim() == "selfemployed" && c.businessType.trim() == "") {
                    emptyFieldExists = true;
                    errors["businessType"] = "Enter a business type.";
                }

                if (c.hasOvertimeIncome && c.overtimeIncomeText.trim() == "") {
                    emptyFieldExists = true;
                    errors["overtimeIncome"] = "Enter an amount.";
                }
                if (c.hasOvertimeIncome && c.overtimeIncomeFrequency.trim() == "") {
                    emptyFieldExists = true;
                    errors["overtimeIncomeFrequency"] = "Select an option.";
                }

                if (c.hasBonusIncome && c.bonusIncomeText.trim() == "") {
                    emptyFieldExists = true;
                    errors["bonusIncome"] = "Enter an amount.";
                }
                if (c.hasBonusIncome && c.bonusIncomeFrequency.trim() == "") {
                    emptyFieldExists = true;
                    errors["bonusIncomeFrequency"] = "Select an option.";
                }

                if (c.hasCommissionIncome && c.commissionIncomeText.trim() == "") {
                    emptyFieldExists = true;
                    errors["commissionIncome"] = "Enter an amount.";
                }
                if (c.hasCommissionIncome && c.commissionIncomeFrequency.trim() == "") {
                    emptyFieldExists = true;
                    errors["commissionIncomeFrequency"] = "Select an option.";
                }

                if (c.hasTipsIncome && c.tipsIncomeText.trim() == "") {
                    emptyFieldExists = true;
                    errors["tipsIncome"] = "Enter an amount.";
                }
                if (c.hasTipsIncome && c.tipsIncomeFrequency.trim() == "") {
                    emptyFieldExists = true;
                    errors["tipsIncomeFrequency"] = "Select an option.";
                }

                if (c.isProbation.trim() == "") {
                    emptyFieldExists = true;
                    errors["isProbation"] = "Select an option.";
                }
                if (c.isFamilyOwned.trim() == "") {
                    emptyFieldExists = true;
                    errors["isFamilyOwned"] = "Select an option.";
                }
                if (c.isOnLeave.trim() == "") {
                    emptyFieldExists = true;
                    errors["isOnLeave"] = "Select an option.";
                }

                if (c.isOnLeave.trim() === "yes" && c.leaveType.trim() == "") {
                    emptyFieldExists = true;
                    errors["leaveType"] = "Select an option.";
                }

                if (c.isOnLeave.trim() === "yes" && c.leaveType.trim() == "other" && c.otherLeaveDescription.trim() == "") {
                    emptyFieldExists = true;
                    errors["otherLeaveDesc"] = "Enter a description.";
                }

                if (c.isOnLeave.trim() === "yes" && c.workReturnDate.trim() == "") {
                    emptyFieldExists = true;
                    errors["workReturnDate"] = "Enter a date of return.";
                }

                if (c.payrollType.trim() == "" && !c.noRetirementIncome) {
                    emptyFieldExists = true;
                    errors["payrollType"] = "Select an option.";
                }
                if (c.income == 0 && !c.noRetirementIncome) {
                    emptyFieldExists = true;
                    errors["income"] = "Enter an amount.";
                }
                if (c.payrollFrequency.trim() == "" && !c.noRetirementIncome) {
                    emptyFieldExists = true;
                    errors["payrollFrequency"] = "Select an option.";
                }
            }

            if (c.status.trim() === "retired" && retiredDeclared) {
                emptyFieldExists = true;
                errors["employmentStatus"] = "Retirement cannot be declared more than once.";
            } else if (c.status.trim() === "retired" && !retiredDeclared) {
                retiredDeclared = true;
                let cppDeclared = false;
                let oasDeclared = false;
                let permanentDisabilityDeclared = false;
                c.otherIncome.forEach((otherIncome, index) => {
                    if (otherIncome.type.trim() == "") {
                        emptyFieldExists = true;
                        errors["otherIncomeType-" + otherIncome.displayId] = "Select an option.";
                    }
                    if (otherIncome.type.trim() == "") {
                        emptyFieldExists = true;
                        errors["otherIncomeAmount-" + otherIncome.displayId] = "Enter an amount.";
                    }
                    if (otherIncome.frequency.trim() == "") {
                        emptyFieldExists = true;
                        errors["otherIncomeFrequency-" + otherIncome.displayId] = "Select an option.";
                    }
                    if (otherIncome.type.trim() == "cpp" && cppDeclared) {
                        emptyFieldExists = true;
                        errors["otherIncomeType-" + otherIncome.displayId] = "CPP cannot be declared more than once.";
                    } else if (otherIncome.type.trim() == "cpp" && !cppDeclared) {
                        cppDeclared = true;
                    }
                    if (otherIncome.type.trim() == "oas" && oasDeclared) {
                        emptyFieldExists = true;
                        errors["otherIncomeType-" + otherIncome.displayId] = "OAS cannot be declared more than once.";
                    } else if (otherIncome.type.trim() == "oas" && !oasDeclared) {
                        oasDeclared = true;
                    }
                    if (otherIncome.type.trim() == "permanentdisability" && permanentDisabilityDeclared) {
                        emptyFieldExists = true;
                        errors["otherIncomeType-" + otherIncome.displayId] = "Permanent Disability cannot be declared more than once.";
                    } else if (otherIncome.type.trim() == "permanentdisability" && !permanentDisabilityDeclared) {
                        permanentDisabilityDeclared = true;
                    }
                });
            }

            if (c.status.trim() === "unemployed" && unemployedDeclared) {
                emptyFieldExists = true;
                errors["employmentStatus"] = "Unemployment cannot be declared more than once.";
            } else if (c.status.trim() === "unemployed" && !unemployedDeclared) {
                unemployedDeclared = true;
            }

            if (c.status.trim() == "retired" || c.status.trim() == "unemployed") {
                if (c.notWorkingYears == undefined) {
                    emptyFieldExists = true;
                    errors["notWorkingYears"] = "Enter the number of years.";
                }
                if (c.notWorkingMonths == undefined) {
                    emptyFieldExists = true;
                    errors["notWorkingMonths"] = "Enter the number of months.";
                }
                if (c.notWorkingYears == 0 && c.notWorkingMonths == 0) {
                    emptyFieldExists = true;
                    errors["notWorkingYears"] = "Enter the number of years.";
                    errors["notWorkingMonths"] = "Enter the number of months.";
                }
            }

            if (c.status.trim() == "employed" || c.status.trim() == "selfemployed") {

                let empAddress = c.employerAddress;
                if (!c.manualAddressEntered && (empAddress.streetNumber.trim() == "" || empAddress.streetName.trim() == "" || empAddress.city.trim() == "" || empAddress.country.trim() == "")) {
                    emptyFieldExists = true;
                    errors["autoAddress"] = "Select an address";
                }
                if (empAddress.streetNumber.trim() == "") {
                    emptyFieldExists = true;
                    errors["streetNumber"] = "Enter your street number.";
                }
                if (empAddress.streetType.trim() == "") {
                    emptyFieldExists = true;
                    errors["streetType"] = "Select a street type.";
                }
                if (empAddress.streetName.trim() == "") {
                    emptyFieldExists = true;
                    errors["streetName"] = "Enter your street name.";
                }
                if (empAddress.unitType.trim() != "" && empAddress.unitNumber.trim() == "") {
                    emptyFieldExists = true;
                    errors["unitNumber"] = "Enter your unit number.";
                }
                if (empAddress.unitNumber.trim() != "" && empAddress.unitType.trim() == "") {
                    emptyFieldExists = true;
                    errors["unitType"] = "Enter your unit type.";
                }
                if (empAddress.city.trim() == "") {
                    emptyFieldExists = true;
                    errors["city"] = "Enter your city name.";
                }
                if (empAddress.province.trim() == "" && (empAddress.country.trim() == "Canada" || empAddress.country.trim() == "United States")) {
                    emptyFieldExists = true;
                    errors["province"] = "Enter your province name.";
                }
                if (empAddress.postalCode.trim() == "" && (empAddress.country.trim() == "Canada" || empAddress.country.trim() == "United States")) {
                    emptyFieldExists = true;
                    errors["postalCode"] = "Enter your postal code.";
                }
                if (empAddress.country.trim() == "") {
                    emptyFieldExists = true;
                    errors["country"] = "Enter your country.";
                }
            }

            if (c.status.trim() == "selfemployed") {
                if (c.hasBusinessLending.trim() == "") {
                    emptyFieldExists = true;
                    errors["businessLendingFromMCU"] = "Select an option.";
                }
            }

            if (Object.keys(errors).length > 0) {
                let newEmploymentInfo: currentEmploymentInfo = { ...c }
                newEmploymentInfo.visible = true;
                dispatch(setCurrentEmploymentInfo(newEmploymentInfo));
            }

            employmentErrors.push(errors);
        });
        setEmpErrors(employmentErrors);
        if (emptyFieldExists) {
            setErrorMessage("One or more fields is missing in your employer(s).");
            setDisplayError(true);
            window.scroll(0, 0);
            let errorMessages: string[] = [];
            // Traverse through the employmentErrors array and log each error object
            employmentErrors.forEach((errorObj, idx) => {
                Object.entries(errorObj).forEach(([key, value]) => {
                    errorMessages.push(`Employment ${idx + 1} - ${key}: ${value}`);
                });
            });
            setClickInsightManualFormErrors(errorMessages);
            return;
        }
        setIsSaving(true);

        let currEmployment: EmploymentDto[] = [];
        let altIncome: OtherIncome[] = [];
        currentEmployment.forEach((c) => {

            //Consolidate Incomes
            let incomes: EmploymentIncomeDto[] = [];
            incomes.push(new EmploymentIncomeDto({
                id: c.incomeId,
                applicantId: applicant.id,
                dealId: application.id,
                incomeCategoryType_Name: "employment",
                incomeType_Name: c.status == "retired" ? "salary" : c.payrollType,
                currencyType_Name: "cad",
                frequencyType_Name: c.payrollFrequency,
                amountPerFrequency: c.income,
                primaryIncomeSource: true,
                employmentId: c.id
            }));

            if (c.hasOvertimeIncome) {
                incomes.push(new EmploymentIncomeDto({
                    id: c.overtimeIncomeId,
                    applicantId: applicant.id,
                    dealId: application.id,
                    incomeCategoryType_Name: "employment",
                    incomeType_Name: "overtime",
                    currencyType_Name: "cad",
                    frequencyType_Name: c.overtimeIncomeFrequency,
                    amountPerFrequency: c.overtimeIncome,
                    primaryIncomeSource: false,
                    employmentId: c.id
                }));
            }
            if (c.hasBonusIncome) {
                incomes.push(new EmploymentIncomeDto({
                    id: c.bonusIncomeId,
                    applicantId: applicant.id,
                    dealId: application.id,
                    incomeCategoryType_Name: "employment",
                    incomeType_Name: "bonus",
                    currencyType_Name: "cad",
                    frequencyType_Name: c.bonusIncomeFrequency,
                    amountPerFrequency: c.bonusIncome,
                    primaryIncomeSource: false,
                    employmentId: c.id
                }));
            }
            if (c.hasCommissionIncome) {
                incomes.push(new EmploymentIncomeDto({
                    id: c.commissionIncomeId,
                    applicantId: applicant.id,
                    dealId: application.id,
                    incomeCategoryType_Name: "employment",
                    incomeType_Name: "commission",
                    currencyType_Name: "cad",
                    frequencyType_Name: c.commissionIncomeFrequency,
                    amountPerFrequency: c.commissionIncome,
                    primaryIncomeSource: false,
                    employmentId: c.id
                }));
            }
            if (c.hasTipsIncome) {
                incomes.push(new EmploymentIncomeDto({
                    id: c.tipsIncomeId,
                    applicantId: applicant.id,
                    dealId: application.id,
                    incomeCategoryType_Name: "employment",
                    incomeType_Name: "declaredtips",
                    currencyType_Name: "cad",
                    frequencyType_Name: c.tipsIncomeFrequency,
                    amountPerFrequency: c.tipsIncome,
                    primaryIncomeSource: false,
                    employmentId: c.id
                }));
            }

            let nonWorkingMonths = (c.notWorkingYears ? (c.notWorkingYears * 12) : 0) + (c.notWorkingMonths ? c.notWorkingMonths : 0);

            let returnDate: dayjs.Dayjs | undefined = undefined;
            if (c.workReturnDate != undefined && c.workReturnDate != "") {
                let dateComponents = c.workReturnDate.split("/");
                returnDate = dayjs(dateComponents[2] + "-" + dateComponents[1] + "-" + dateComponents[0]);
            }

            //If the employment changed from employed/self-employed to retired/unemployed, reset the employment id to zero
            let prevCurrentEmployment = sessionData.cachedCurrentEmployment?.find((emp) => emp.id == c.id);
            let employmentId = c.id;
            if (prevCurrentEmployment && (prevCurrentEmployment.status == "employed" || prevCurrentEmployment.status == "selfemployed") && (c.status == "retired" || c.status == "unemployed")) {
                employmentId = 0;
            }

            currEmployment.push(new EmploymentDto({
                id: employmentId,
                employmentType_Name: c.status,
                employmentPositionType_Name: c.type,
                jobTitle: c.jobTitle,
                employer_Name: c.employerName,
                employer_Phone: c.employerPhone,
                employer_Address_StreetNumber: c.employerAddress.streetNumber,
                employer_Address_StreetName: c.employerAddress.streetName,
                employer_Address_StreetType: c.employerAddress.streetType,
                employer_Address_StreetDirection: c.employerAddress.streetDirection,
                employer_Address_UnitType: c.employerAddress.unitType,
                employer_Address_UnitNumber: c.employerAddress.unitNumber,
                employer_Address_City: c.employerAddress.city,
                employer_Address_PostalCode: c.employerAddress.postalCode,
                employer_Address_Region: c.employerAddress.province,
                employer_Address_Country: c.employerAddress.country,
                monthsAtEmployment: (c.status == "retired" || c.status == "unemployed") ? nonWorkingMonths
                    : (c.employmentYears ? (c.employmentYears * 12) : 0) + (c.employmentMonths ? c.employmentMonths : 0),
                monthsInIndustry: (c.industryYears ? (c.industryYears * 12) : 0) + (c.industryMonths ? c.industryMonths : 0),
                totalMonthsAtEmployment: (c.status == "retired" || c.status == "unemployed") ? nonWorkingMonths
                    : (c.employmentYears ? (c.employmentYears * 12) : 0) + (c.employmentMonths ? c.employmentMonths : 0),
                totalMonthsInIndustry: (c.industryYears ? (c.industryYears * 12) : 0) + (c.industryMonths ? c.industryMonths : 0),
                employer_Occupation_Industry: c.occupationIndustry,
                employer_Occupation_Name: c.occupation,
                ownership_OwnershipType_Name: c.ownershipType,
                ownership_BusinessType: c.businessType,
                ownership_HasLendingWithMeridian: c.hasBusinessLending === "yes",
                previousEmployment: false,
                employmentIncomes: c.status == "unemployed" || c.status == "retired" ? [] : incomes,
                onProbation: c.isProbation == "yes",
                familyOwned: c.isFamilyOwned == "yes",
                onParentalLeave: c.leaveType == "parental",
                onOtherLeave: c.isOnLeave == "yes" && c.leaveType == "other",
                leaveDescription: c.otherLeaveDescription,
                returnToWorkDate: c.workReturnDate ? returnDate : undefined,
            }));
            if (c.status == "retired" && !c.noRetirementIncome) {
                c.otherIncome.forEach((income) => {
                    altIncome.push(new OtherIncome({
                        id: income.id,
                        applicantId: applicant.id,
                        dealId: application.id,
                        incomeCategoryType_Name: "other",
                        incomeType_Name: income.type,
                        currencyType_Name: "cad",
                        frequencyType_Name: income.frequency,
                        amountPerFrequency: income.amount,
                        primaryIncomeSource: true,
                    }));
                })
            }
        });

        await client.apiEmploymentsCurrent(new EmploymentModel({
            employment: currEmployment,
            otherIncome: altIncome
        })).then((result) => {
            let highestEmploymentTime = 0;
            if (result != undefined && result.length > 0) {
                result.forEach((emp, index) => {
                    if (!emp.previousEmployment) {
                        currentEmployment.forEach((c) => {
                            if ((c.occupation == emp.employer_Occupation_Name && c.jobTitle == emp.jobTitle && c.status !== "retired")
                                || (c.status == "retired" && c.status == emp.employmentType_Name) || (c.status == "unemployed" && c.status == emp.employmentType_Name)) {
                                let newEmp: currentEmploymentInfo = { ...c }
                                newEmp.id = emp.id ? emp.id : 0;

                                //Map retirement income IDs into employment store
                                if (emp.employmentType_Name == "retired") {
                                    //Map income IDs into current employment store
                                    let assignedIDs: (number | undefined)[] = [];
                                    newEmp.otherIncome = [];
                                    emp.employmentIncomes?.forEach((income) => {
                                        c.otherIncome.forEach((otherIncome) => {
                                            if (otherIncome.type == income.incomeType_Name && otherIncome.amount == income.amountPerFrequency
                                                && !assignedIDs.includes(income.id)) {
                                                let newOtherIncome = { ...otherIncome, id: income.id ? income.id : 0 };
                                                newEmp.otherIncome.push(newOtherIncome);
                                                assignedIDs.push(income.id);
                                            }
                                        });
                                    });
                                } else {
                                    //Map income IDs into current employment store
                                    emp.employmentIncomes?.forEach((income) => {
                                        switch (income.incomeType_Name) {
                                            case "salary":
                                                newEmp.incomeId = income.id ? income.id : 0;
                                                break;
                                            case "overtime":
                                                newEmp.overtimeIncomeId = income.id ? income.id : 0;
                                                break;
                                            case "bonus":
                                                newEmp.bonusIncomeId = income.id ? income.id : 0;
                                                break;
                                            case "commission":
                                                newEmp.commissionIncomeId = income.id ? income.id : 0;
                                                break;
                                            case "declaredtips":
                                                newEmp.tipsIncomeId = income.id ? income.id : 0;
                                                break;
                                            default:
                                                newEmp.incomeId = income.id ? income.id : 0;
                                                break;
                                        }
                                    });
                                }


                                dispatch(setCurrentEmploymentInfo(newEmp));
                            }
                        });
                        if (emp.monthsAtEmployment && emp.monthsAtEmployment > highestEmploymentTime) {
                            highestEmploymentTime = emp.monthsAtEmployment;
                        }
                    }
                });
            }
            setClickInsightApplicationProgress('Build your profile - What is your current work situation?');
            if (highestEmploymentTime < 36) {
                dispatch(setApplicationProgress(11));
                navigate("/single/previous-employment", { state: { editMode: editMode } });
            } else {
                dispatch(setApplicationProgress(12));
                if (editMode) {
                    navigate(-1);
                } else {
                    if (application.applicationCategory !== applicationCategory.Buy) {
                        navigate("/single/refi-property-details");
                    } else {
                        navigate("/single/assets");
                    }
                }
            }
        }).catch((error) => {
            setIsSaving(false);
            console.log("Error saving employment details", error);
        });

    };

    const getAddressData = (id: any, type: any, val: any) => {
        let addressData = { ...currentEmployment[id - 1] };

        let newEmpAddressData = { ...addressData.employerAddress }

        switch (type) {
            case "streetNumber":
                newEmpAddressData.streetNumber = val;
                break;
            case "streetName":
                newEmpAddressData.streetName = val;
                break;
            case "streetType":
                newEmpAddressData.streetType = val;
                break;
            case "streetDirection":
                newEmpAddressData.streetDirection = val;
                break;
            case "unitNumber":
                newEmpAddressData.unitNumber = val;
                break;
            case "unitType":
                newEmpAddressData.unitType = val;
                break;
            case "cityName":
                newEmpAddressData.city = val;
                break;
            case "province":
                newEmpAddressData.province = val;
                break;
            case "postalCode":
                newEmpAddressData.postalCode = val;
                break;
            case "country":
                newEmpAddressData.country = val;
                //Clear the province/state if country is updated
                newEmpAddressData.province = "";
                break;
        }
        setEmployerAddress(addressData.displayId, newEmpAddressData);

        if (empErrors[parseInt(id) - 1]["streetNumber"] != undefined && type == "streetNumber" && val.trim() != "") {
            let newEmpErrors = [...empErrors];
            delete newEmpErrors[parseInt(id) - 1]["streetNumber"];
            setEmpErrors(newEmpErrors);
        }
        if (empErrors[parseInt(id) - 1]["streetName"] != undefined && type == "streetName" && val.trim() != "") {
            let newEmpErrors = [...empErrors];
            delete newEmpErrors[parseInt(id) - 1]["streetName"];
            setEmpErrors(newEmpErrors);
        }
        if (empErrors[parseInt(id) - 1]["streetType"] != undefined && type == "streetType" && val.trim() != "") {
            let newEmpErrors = [...empErrors];
            delete newEmpErrors[parseInt(id) - 1]["streetType"];
            setEmpErrors(newEmpErrors);
        }
        if (empErrors[parseInt(id) - 1]["city"] != undefined && type == "cityName" && val.trim() != "") {
            let newEmpErrors = [...empErrors];
            delete newEmpErrors[parseInt(id) - 1]["city"];
            setEmpErrors(newEmpErrors);
        }
        if (empErrors[parseInt(id) - 1]["province"] != undefined && type == "province" && val.trim() != "") {
            let newEmpErrors = [...empErrors];
            delete newEmpErrors[parseInt(id) - 1]["province"];
            setEmpErrors(newEmpErrors);
        }
        if (empErrors[parseInt(id) - 1]["postalCode"] != undefined && type == "postalCode" && val.trim() != "") {
            let newEmpErrors = [...empErrors];
            delete newEmpErrors[parseInt(id) - 1]["postalCode"];
            setEmpErrors(newEmpErrors);
        }
        if (empErrors[parseInt(id) - 1]["country"] != undefined && type == "country" && val.trim() != "") {
            let newEmpErrors = [...empErrors];
            delete newEmpErrors[parseInt(id) - 1]["country"];
            setEmpErrors(newEmpErrors);
        }

        //If Street Number, Steet Name and City are Filled, Clear AutoAddress Error (if it exists)
        if (newEmpAddressData.streetNumber.trim() != "" && newEmpAddressData.streetName.trim() != ""
            && newEmpAddressData.streetType.trim() != "" && newEmpAddressData.city.trim() != "") {
            let newEmpErrors = [...empErrors];
            delete newEmpErrors[parseInt(id) - 1]["autoAddress"];
            setEmpErrors(newEmpErrors);
        }
    }

    const toggleManualAddress = (id: string) => {
        currentEmployment.forEach(c => {
            if (c.displayId == id) {
                let newEmploymentInfo: currentEmploymentInfo = { ...c }
                newEmploymentInfo.manualAddressEntered = !newEmploymentInfo.manualAddressEntered;
                dispatch(setCurrentEmploymentInfo(newEmploymentInfo));
            }
        });
    };

    const addCurrEmployment = () => {
        let employmentId = (currentEmployment.length + 1).toString();

        //Auto hide other employments (if they're visible)
        currentEmployment.forEach(c => {
            if (c.displayId != employmentId) {
                let newEmploymentInfo = { ...c }
                newEmploymentInfo.visible = false;
                dispatch(setCurrentEmploymentInfo(newEmploymentInfo));
            }
        });

        let newEmploymentInfo: currentEmploymentInfo = {
            id: 0,
            displayId: employmentId,
            visible: true,
            status: "",
            type: "",
            occupation: "",
            occupationIndustry: "",
            jobTitle: "",
            ownershipType: "",
            businessType: "",
            hasBusinessLending: "",
            industryYears: undefined,
            industryMonths: undefined,
            employmentYears: undefined,
            employmentMonths: undefined,
            notWorkingYears: undefined,
            notWorkingMonths: undefined,
            payrollType: "",
            income: 0,
            incomeId: 0,
            incomeText: "",
            payrollFrequency: "",
            noRetirementIncome: false,
            hasOvertimeIncome: false,
            overtimeIncomeId: 0,
            overtimeIncome: 0,
            overtimeIncomeText: "",
            overtimeIncomeFrequency: "",
            hasBonusIncome: false,
            bonusIncomeId: 0,
            bonusIncome: 0,
            bonusIncomeText: "",
            bonusIncomeFrequency: "",
            hasCommissionIncome: false,
            commissionIncomeId: 0,
            commissionIncome: 0,
            commissionIncomeText: "",
            commissionIncomeFrequency: "",
            hasTipsIncome: false,
            tipsIncomeId: 0,
            tipsIncome: 0,
            tipsIncomeText: "",
            tipsIncomeFrequency: "",
            otherIncome: [],

            employerName: "",
            employerPhone: "",
            employerAddress: {
                displayId: employmentId,
                id: 0,
                streetNumber: "",
                streetName: "",
                streetType: "",
                streetDirection: "",
                unitType: "",
                unitNumber: "",
                city: "",
                province: "",
                postalCode: "",
                country: "",
            },
            manualAddressEntered: false,
            isProbation: "",
            isFamilyOwned: "",
            isOnLeave: "",
            leaveType: "",
            otherLeaveDescription: "",
            workReturnDate: ""
        }
        dispatch(addCurrentEmployment(newEmploymentInfo));
    }

    const clearEmploymentFields = (id: number, displayId: string, status: string) => {
        let updatedEmploymentInfo: currentEmploymentInfo = {
            id: id,
            displayId: displayId,
            visible: true,
            status: status,
            type: "",
            occupation: "",
            occupationIndustry: "",
            jobTitle: "",
            ownershipType: "",
            businessType: "",
            hasBusinessLending: "",
            industryYears: undefined,
            industryMonths: undefined,
            employmentYears: undefined,
            employmentMonths: undefined,
            notWorkingYears: undefined,
            notWorkingMonths: undefined,
            payrollType: "",
            income: 0,
            incomeId: 0,
            incomeText: "",
            payrollFrequency: "",
            noRetirementIncome: false,
            hasOvertimeIncome: false,
            overtimeIncomeId: 0,
            overtimeIncome: 0,
            overtimeIncomeText: "",
            overtimeIncomeFrequency: "",
            hasBonusIncome: false,
            bonusIncomeId: 0,
            bonusIncome: 0,
            bonusIncomeText: "",
            bonusIncomeFrequency: "",
            hasCommissionIncome: false,
            commissionIncomeId: 0,
            commissionIncome: 0,
            commissionIncomeText: "",
            commissionIncomeFrequency: "",
            hasTipsIncome: false,
            tipsIncomeId: 0,
            tipsIncome: 0,
            tipsIncomeText: "",
            tipsIncomeFrequency: "",
            otherIncome: status === "retired" ? [{
                id: 0,
                displayId: "1",
                type: "",
                amount: 0,
                amountText: "",
                frequency: ""
            }] : [],

            employerName: "",
            employerPhone: "",
            employerAddress: {
                displayId: "",
                id: 0,
                streetNumber: "",
                streetName: "",
                streetType: "",
                streetDirection: "",
                unitType: "",
                unitNumber: "",
                city: "",
                province: "",
                postalCode: "",
                country: "",
            },
            manualAddressEntered: false,
            isProbation: "",
            isFamilyOwned: "",
            isOnLeave: "",
            leaveType: "",
            otherLeaveDescription: "",
            workReturnDate: ""
        }
        dispatch(setCurrentEmploymentInfo(updatedEmploymentInfo));
    }

    const addOtherIncome = (id: string) => {
        //Check if user selected multiple CPP, OAS or Permanent Disability Options
        let emptyFieldExists = false;
        let employmentErrors: Record<string, string>[] = [];

        currentEmployment.forEach((c, index) => {
            let errors: Record<string, string> = {};
            if (c.status.trim() === "retired") {
                let cppDeclared = false;
                let oasDeclared = false;
                let permanentDisabilityDeclared = false;
                c.otherIncome.forEach((otherIncome, index) => {
                    if (otherIncome.type.trim() == "") {
                        emptyFieldExists = true;
                        errors["otherIncomeType-" + otherIncome.displayId] = "Select an option.";
                    }
                    if (otherIncome.type.trim() == "") {
                        emptyFieldExists = true;
                        errors["otherIncomeAmount-" + otherIncome.displayId] = "Enter an amount.";
                    }
                    if (otherIncome.frequency.trim() == "") {
                        emptyFieldExists = true;
                        errors["otherIncomeFrequency-" + otherIncome.displayId] = "Select an option.";
                    }
                    if (otherIncome.type.trim() == "cpp" && cppDeclared) {
                        emptyFieldExists = true;
                        errors["otherIncomeType-" + otherIncome.displayId] = "CPP cannot be declared more than once.";
                    } else if (otherIncome.type.trim() == "cpp" && !cppDeclared) {
                        cppDeclared = true;
                    }
                    if (otherIncome.type.trim() == "oas" && oasDeclared) {
                        emptyFieldExists = true;
                        errors["otherIncomeType-" + otherIncome.displayId] = "OAS cannot be declared more than once.";
                    } else if (otherIncome.type.trim() == "oas" && !oasDeclared) {
                        oasDeclared = true;
                    }
                    if (otherIncome.type.trim() == "permanentdisability" && permanentDisabilityDeclared) {
                        emptyFieldExists = true;
                        errors["otherIncomeType-" + otherIncome.displayId] = "Permanent Disability cannot be declared more than once.";
                    } else if (otherIncome.type.trim() == "permanentdisability" && !permanentDisabilityDeclared) {
                        permanentDisabilityDeclared = true;
                    }
                });
            }
            employmentErrors.push(errors);
        });
        setEmpErrors(employmentErrors);
        if (emptyFieldExists) {
            window.scroll(0, 0);
            console.log(employmentErrors);
            return;
        }

        currentEmployment.forEach(c => {
            if (c.displayId == id) {
                let newEmploymentInfo = { ...c }
                newEmploymentInfo.otherIncome = newEmploymentInfo.otherIncome ? [...newEmploymentInfo.otherIncome] : [];
                let currentIndex = newEmploymentInfo.otherIncome?.length;
                newEmploymentInfo.otherIncome.push({
                    id: 0,
                    displayId: (currentIndex + 1).toString(),
                    type: "",
                    amount: 0,
                    amountText: "",
                    frequency: ""
                });
                dispatch(setCurrentEmploymentInfo(newEmploymentInfo));
            }
        });
    }

    const removeOtherIncome = (id: string, incomeDisplayId: string) => {
        currentEmployment.forEach(c => {
            if (c.displayId == id) {
                let newEmploymentInfo = { ...c }
                newEmploymentInfo.otherIncome = [];
                c.otherIncome?.forEach(oi => {
                    if (oi.displayId != incomeDisplayId) {
                        newEmploymentInfo.otherIncome.push(oi);
                    }
                });
                dispatch(setCurrentEmploymentInfo(newEmploymentInfo));
            }
        });
    }

    const setOtherIncomeType = (id: string, incomeDisplayId: string, newType: string) => {
        currentEmployment.forEach(c => {
            if (c.displayId == id) {
                let newEmploymentInfo = { ...c }
                newEmploymentInfo.otherIncome = newEmploymentInfo.otherIncome.map(oi => {
                    if (oi.displayId == incomeDisplayId) {
                        if (empErrors[parseInt(id) - 1]["otherIncomeType-" + oi.displayId] != undefined && newType.trim() != "") {
                            let newEmpErrors = [...empErrors];
                            delete newEmpErrors[parseInt(id) - 1]["otherIncomeType-" + oi.displayId];
                            setEmpErrors(newEmpErrors);
                        }
                        return { ...oi, type: newType }
                    }
                    return oi;
                });
                dispatch(setCurrentEmploymentInfo(newEmploymentInfo));
            }
        });
    }

    const setOtherIncomeAmount = (id: string, incomeDisplayId: string, newAmount: string) => {
        currentEmployment.forEach(c => {
            if (c.displayId == id) {
                let newEmploymentInfo = { ...c }
                newEmploymentInfo.otherIncome = newEmploymentInfo.otherIncome.map(oi => {
                    if (oi.displayId == incomeDisplayId) {
                        if (empErrors[parseInt(id) - 1]["otherIncomeAmount-" + oi.displayId] != undefined && newAmount.trim() != "") {
                            let newEmpErrors = [...empErrors];
                            delete newEmpErrors[parseInt(id) - 1]["otherIncomeAmount-" + oi.displayId];
                            setEmpErrors(newEmpErrors);
                        }
                        return { ...oi, amount: +newAmount, amountText: newAmount }
                    }
                    return oi;
                });
                dispatch(setCurrentEmploymentInfo(newEmploymentInfo));
            }
        });
    }

    const setOtherIncomeFrequency = (id: string, incomeDisplayId: string, newFrequency: string) => {
        currentEmployment.forEach(c => {
            if (c.displayId == id) {
                let newEmploymentInfo = { ...c }
                newEmploymentInfo.otherIncome = newEmploymentInfo.otherIncome.map(oi => {
                    if (oi.displayId == incomeDisplayId) {
                        if (empErrors[parseInt(id) - 1]["otherIncomeFrequency-" + oi.displayId] != undefined && newFrequency.trim() != "") {
                            let newEmpErrors = [...empErrors];
                            delete newEmpErrors[parseInt(id) - 1]["otherIncomeFrequency-" + oi.displayId];
                            setEmpErrors(newEmpErrors);
                        }
                        return { ...oi, frequency: newFrequency }
                    }
                    return oi;
                });
                dispatch(setCurrentEmploymentInfo(newEmploymentInfo));
            }
        });
    }

    const toggleEmployerVisibility = (id: string) => {
        currentEmployment.forEach(c => {
            if (c.displayId == id) {
                let newEmploymentInfo = { ...c }
                newEmploymentInfo.visible = !newEmploymentInfo.visible;
                dispatch(setCurrentEmploymentInfo(newEmploymentInfo));
            }
        });
    }

    const setEmploymentStatus = (id: string, newStatus: string) => {
        currentEmployment.forEach(c => {
            if (c.displayId == id) {
                let newEmploymentInfo = { ...c }
                newEmploymentInfo.status = newStatus;
                if (newStatus != "retired") {
                    newEmploymentInfo.noRetirementIncome = false;
                }

                //Clear other income if employed or selfemployed are selected
                if (newStatus === "employed" || newStatus === "selfemployed") {
                    newEmploymentInfo.otherIncome = [];
                }

                //Reset Additional Income Fields after a Status Change
                newEmploymentInfo.hasOvertimeIncome = false;
                newEmploymentInfo.overtimeIncome = 0;
                newEmploymentInfo.overtimeIncomeText = "";
                newEmploymentInfo.overtimeIncomeFrequency = "";
                newEmploymentInfo.hasBonusIncome = false;
                newEmploymentInfo.bonusIncome = 0;
                newEmploymentInfo.bonusIncomeText = "";
                newEmploymentInfo.bonusIncomeFrequency = "";
                newEmploymentInfo.hasCommissionIncome = false;
                newEmploymentInfo.commissionIncome = 0;
                newEmploymentInfo.commissionIncomeText = "";
                newEmploymentInfo.commissionIncomeFrequency = "";
                newEmploymentInfo.hasTipsIncome = false;
                newEmploymentInfo.tipsIncome = 0;
                newEmploymentInfo.tipsIncomeText = "";
                newEmploymentInfo.tipsIncomeFrequency = "";
                dispatch(setCurrentEmploymentInfo(newEmploymentInfo));
            }
        });
        if (empErrors[parseInt(id) - 1]["employmentStatus"] != undefined && newStatus.trim() != "") {
            let newEmpErrors = [...empErrors];
            delete newEmpErrors[parseInt(id) - 1]["employmentStatus"];
            setEmpErrors(newEmpErrors);
        }
    };

    const setEmploymentType = (id: string, newType: string) => {
        currentEmployment.forEach(c => {
            if (c.displayId == id) {
                let newEmploymentInfo: currentEmploymentInfo = { ...c }
                newEmploymentInfo.type = newType;
                dispatch(setCurrentEmploymentInfo(newEmploymentInfo));
            }
        });

        if (empErrors[parseInt(id) - 1]["employmentType"] != undefined && newType.trim() != "") {
            let newEmpErrors = [...empErrors];
            delete newEmpErrors[parseInt(id) - 1]["employmentType"];
            setEmpErrors(newEmpErrors);
        }
    };

    const setNotWorkingLength = (id: string, years?: string, months?: string) => {
        currentEmployment.forEach(c => {
            if (c.displayId == id) {
                let newEmploymentInfo: currentEmploymentInfo = { ...c }
                newEmploymentInfo.notWorkingYears = years != undefined ? +years : c.notWorkingYears,
                    newEmploymentInfo.notWorkingMonths = months != undefined ? +months : c.notWorkingMonths,
                    dispatch(setCurrentEmploymentInfo(newEmploymentInfo));

                if (empErrors[parseInt(id) - 1]["notWorkingYears"] != undefined && years != undefined && years.trim() != "") {
                    let newEmpErrors = [...empErrors];
                    delete newEmpErrors[parseInt(id) - 1]["notWorkingYears"];
                    setEmpErrors(newEmpErrors);
                } else if (empErrors[parseInt(id) - 1]["notWorkingMonths"] != undefined && months != undefined && months.trim() != "") {
                    let newEmpErrors = [...empErrors];
                    delete newEmpErrors[parseInt(id) - 1]["notWorkingMonths"];
                    setEmpErrors(newEmpErrors);
                }
            }
        });
    };

    const setOccupation = (id: string, newOccupation: string, newIndustry: string) => {
        currentEmployment.forEach(c => {
            if (c.displayId == id) {
                let newEmploymentInfo: currentEmploymentInfo = { ...c }
                newEmploymentInfo.occupation = newOccupation;
                newEmploymentInfo.occupationIndustry = newIndustry;
                dispatch(setCurrentEmploymentInfo(newEmploymentInfo));
            }
        });
        if (empErrors[parseInt(id) - 1]["occupation"] != undefined && newOccupation.trim() != "") {
            let newEmpErrors = [...empErrors];
            delete newEmpErrors[parseInt(id) - 1]["occupation"];
            setEmpErrors(newEmpErrors);
        }
    };

    const setJobTitle = (id: string, newTitle: string) => {
        currentEmployment.forEach(c => {
            if (c.displayId == id) {
                let newEmploymentInfo: currentEmploymentInfo = { ...c }
                newEmploymentInfo.jobTitle = newTitle;
                dispatch(setCurrentEmploymentInfo(newEmploymentInfo));
            }
        });
        if (empErrors[parseInt(id) - 1]["jobTitle"] != undefined && newTitle.trim() != "") {
            let newEmpErrors = [...empErrors];
            delete newEmpErrors[parseInt(id) - 1]["jobTitle"];
            setEmpErrors(newEmpErrors);
        }
    };

    const setOwnership = (id: string, newOwnership: string) => {
        currentEmployment.forEach(c => {
            if (c.displayId == id) {
                let newEmploymentInfo: currentEmploymentInfo = { ...c }
                newEmploymentInfo.ownershipType = newOwnership;
                dispatch(setCurrentEmploymentInfo(newEmploymentInfo));

                if (empErrors[parseInt(id) - 1]["ownership"] != undefined && newOwnership.trim() != "") {
                    let newEmpErrors = [...empErrors];
                    delete newEmpErrors[parseInt(id) - 1]["ownership"];
                    setEmpErrors(newEmpErrors);
                }
            }
        });
    };

    const setBusinessType = (id: string, newType: string) => {
        currentEmployment.forEach(c => {
            if (c.displayId == id) {
                let newEmploymentInfo: currentEmploymentInfo = { ...c }
                newEmploymentInfo.businessType = newType;
                dispatch(setCurrentEmploymentInfo(newEmploymentInfo));
            }
        });
        if (empErrors[parseInt(id) - 1]["businessType"] != undefined && newType.trim() != "") {
            let newEmpErrors = [...empErrors];
            delete newEmpErrors[parseInt(id) - 1]["businessType"];
            setEmpErrors(newEmpErrors);
        }
    };

    const setIndustryLength = (id: string, years?: string, months?: string) => {
        currentEmployment.forEach(c => {
            if (c.displayId == id) {
                let newEmploymentInfo: currentEmploymentInfo = { ...c }
                newEmploymentInfo.industryYears = years != undefined ? +years : c.industryYears,
                    newEmploymentInfo.industryMonths = months != undefined ? +months : c.industryMonths,
                    dispatch(setCurrentEmploymentInfo(newEmploymentInfo));
            }
        });
        if (empErrors[parseInt(id) - 1]["industryLengthYears"] != undefined && (years?.trim() != "")) {
            let newEmpErrors = [...empErrors];
            delete newEmpErrors[parseInt(id) - 1]["industryLengthYears"];
            setEmpErrors(newEmpErrors);
        }
        if (empErrors[parseInt(id) - 1]["industryLengthMonths"] != undefined && (months?.trim() != "")) {
            let newEmpErrors = [...empErrors];
            delete newEmpErrors[parseInt(id) - 1]["industryLengthMonths"];
            setEmpErrors(newEmpErrors);
        }
    };

    const setEmploymentLength = (id: string, years?: string, months?: string) => {
        currentEmployment.forEach(c => {
            if (c.displayId == id) {
                let newEmploymentInfo: currentEmploymentInfo = { ...c }
                newEmploymentInfo.employmentYears = years != undefined ? +years : c.employmentYears,
                    newEmploymentInfo.employmentMonths = months != undefined ? +months : c.employmentMonths,
                    dispatch(setCurrentEmploymentInfo(newEmploymentInfo));
            }
        });
        if (empErrors[parseInt(id) - 1]["employmentYears"] != undefined && (years?.trim() != "")) {
            let newEmpErrors = [...empErrors];
            delete newEmpErrors[parseInt(id) - 1]["employmentYears"];
            setEmpErrors(newEmpErrors);
        }
        if (empErrors[parseInt(id) - 1]["employmentMonths"] != undefined && (months?.trim() != "")) {
            let newEmpErrors = [...empErrors];
            delete newEmpErrors[parseInt(id) - 1]["employmentMonths"];
            setEmpErrors(newEmpErrors);
        }
    };

    const setBusinessLending = (id: string, response: string) => {
        currentEmployment.forEach(c => {
            if (c.displayId == id) {
                let newEmploymentInfo: currentEmploymentInfo = { ...c }
                newEmploymentInfo.hasBusinessLending = response;
                dispatch(setCurrentEmploymentInfo(newEmploymentInfo));
            }
        });
        if (empErrors[parseInt(id) - 1]["businessLendingFromMCU"] != undefined && response.trim() != "") {
            let newEmpErrors = [...empErrors];
            delete newEmpErrors[parseInt(id) - 1]["businessLendingFromMCU"];
            setEmpErrors(newEmpErrors);
        }
    };

    const setNoRetirementIncome = (id: string, selected: boolean) => {
        currentEmployment.forEach(c => {
            if (c.displayId == id) {
                let newEmploymentInfo: currentEmploymentInfo = { ...c }
                newEmploymentInfo.noRetirementIncome = selected;
                dispatch(setCurrentEmploymentInfo(newEmploymentInfo));
            }
        });
    };

    const setPayrollType = (id: string, newPayroll: string) => {
        currentEmployment.forEach(c => {
            if (c.displayId == id) {
                let newEmploymentInfo: currentEmploymentInfo = { ...c }
                newEmploymentInfo.payrollType = newPayroll;
                dispatch(setCurrentEmploymentInfo(newEmploymentInfo));
            }
        });
        if (empErrors[parseInt(id) - 1]["payrollType"] != undefined && newPayroll.trim() != "") {
            let newEmpErrors = [...empErrors];
            delete newEmpErrors[parseInt(id) - 1]["payrollType"];
            setEmpErrors(newEmpErrors);
        }
    };

    const setIncomeAmount = (id: string, newAmount: string) => {
        currentEmployment.forEach(c => {
            if (c.displayId == id) {
                let newEmploymentInfo: currentEmploymentInfo = { ...c }
                newEmploymentInfo.incomeText = newAmount;
                newEmploymentInfo.income = +newAmount;
                dispatch(setCurrentEmploymentInfo(newEmploymentInfo));
            }
        });
        if (empErrors[parseInt(id) - 1]["income"] != undefined && newAmount.trim() != "") {
            let newEmpErrors = [...empErrors];
            delete newEmpErrors[parseInt(id) - 1]["income"];
            setEmpErrors(newEmpErrors);
        }
    };

    const setPayrollFrequency = (id: string, newFrequency: string) => {
        currentEmployment.forEach(c => {
            if (c.displayId == id) {
                let newEmploymentInfo: currentEmploymentInfo = { ...c }
                newEmploymentInfo.payrollFrequency = newFrequency;
                dispatch(setCurrentEmploymentInfo(newEmploymentInfo));
            }
        });
        if (empErrors[parseInt(id) - 1]["payrollFrequency"] != undefined && newFrequency.trim() != "") {
            let newEmpErrors = [...empErrors];
            delete newEmpErrors[parseInt(id) - 1]["payrollFrequency"];
            setEmpErrors(newEmpErrors);
        }
    };

    const setOvertimeSelected = (id: string, overtimeSelected: boolean) => {
        currentEmployment.forEach(c => {
            if (c.displayId == id) {
                let newEmploymentInfo: currentEmploymentInfo = { ...c }
                newEmploymentInfo.hasOvertimeIncome = overtimeSelected;
                if (!overtimeSelected) {
                    newEmploymentInfo.overtimeIncome = 0;
                    newEmploymentInfo.overtimeIncomeText = "";
                    newEmploymentInfo.overtimeIncomeFrequency = "";
                }
                dispatch(setCurrentEmploymentInfo(newEmploymentInfo));
            }
        });
    };

    const setOvertimeIncomeAmount = (id: string, newAmount: string) => {
        currentEmployment.forEach(c => {
            if (c.displayId == id) {
                let newEmploymentInfo: currentEmploymentInfo = { ...c }
                newEmploymentInfo.overtimeIncomeText = newAmount;
                newEmploymentInfo.overtimeIncome = +newAmount;
                dispatch(setCurrentEmploymentInfo(newEmploymentInfo));
            }
        });
        if (empErrors[parseInt(id) - 1]["overtimeIncome"] != undefined && newAmount.trim() != "") {
            let newEmpErrors = [...empErrors];
            delete newEmpErrors[parseInt(id) - 1]["overtimeIncome"];
            setEmpErrors(newEmpErrors);
        }
    };

    const setOvertimeIncomeFrequency = (id: string, newFrequency: string) => {
        currentEmployment.forEach(c => {
            if (c.displayId == id) {
                let newEmploymentInfo: currentEmploymentInfo = { ...c }
                newEmploymentInfo.overtimeIncomeFrequency = newFrequency;
                dispatch(setCurrentEmploymentInfo(newEmploymentInfo));
            }
        });
        if (empErrors[parseInt(id) - 1]["overtimeIncomeFrequency"] != undefined && newFrequency.trim() != "") {
            let newEmpErrors = [...empErrors];
            delete newEmpErrors[parseInt(id) - 1]["overtimeIncomeFrequency"];
            setEmpErrors(newEmpErrors);
        }
    };

    const setBonusSelected = (id: string, bonusSelected: boolean) => {
        currentEmployment.forEach(c => {
            if (c.displayId == id) {
                let newEmploymentInfo: currentEmploymentInfo = { ...c }
                newEmploymentInfo.hasBonusIncome = bonusSelected;
                if (!bonusSelected) {
                    newEmploymentInfo.bonusIncome = 0;
                    newEmploymentInfo.bonusIncomeText = "";
                    newEmploymentInfo.bonusIncomeFrequency = "";
                }
                dispatch(setCurrentEmploymentInfo(newEmploymentInfo));
            }
        });
    };

    const setBonusIncomeAmount = (id: string, newAmount: string) => {
        currentEmployment.forEach(c => {
            if (c.displayId == id) {
                let newEmploymentInfo: currentEmploymentInfo = { ...c }
                newEmploymentInfo.bonusIncomeText = newAmount;
                newEmploymentInfo.bonusIncome = +newAmount;
                dispatch(setCurrentEmploymentInfo(newEmploymentInfo));
            }
        });
        if (empErrors[parseInt(id) - 1]["bonusIncome"] != undefined && newAmount.trim() != "") {
            let newEmpErrors = [...empErrors];
            delete newEmpErrors[parseInt(id) - 1]["bonusIncome"];
            setEmpErrors(newEmpErrors);
        }
    };

    const setBonusIncomeFrequency = (id: string, newFrequency: string) => {
        currentEmployment.forEach(c => {
            if (c.displayId == id) {
                let newEmploymentInfo: currentEmploymentInfo = { ...c }
                newEmploymentInfo.bonusIncomeFrequency = newFrequency;
                dispatch(setCurrentEmploymentInfo(newEmploymentInfo));
            }
        });
        if (empErrors[parseInt(id) - 1]["bonusIncomeFrequency"] != undefined && newFrequency.trim() != "") {
            let newEmpErrors = [...empErrors];
            delete newEmpErrors[parseInt(id) - 1]["bonusIncomeFrequency"];
            setEmpErrors(newEmpErrors);
        }
    };

    const setCommissionSelected = (id: string, commissionSelected: boolean) => {
        currentEmployment.forEach(c => {
            if (c.displayId == id) {
                let newEmploymentInfo: currentEmploymentInfo = { ...c }
                newEmploymentInfo.hasCommissionIncome = commissionSelected;
                if (!commissionSelected) {
                    newEmploymentInfo.commissionIncome = 0;
                    newEmploymentInfo.commissionIncomeText = "";
                    newEmploymentInfo.commissionIncomeFrequency = "";
                }
                dispatch(setCurrentEmploymentInfo(newEmploymentInfo));
            }
        });
    };

    const setCommissionIncomeAmount = (id: string, newAmount: string) => {
        currentEmployment.forEach(c => {
            if (c.displayId == id) {
                let newEmploymentInfo: currentEmploymentInfo = { ...c }
                newEmploymentInfo.commissionIncomeText = newAmount;
                newEmploymentInfo.commissionIncome = +newAmount;
                dispatch(setCurrentEmploymentInfo(newEmploymentInfo));
            }
        });
        if (empErrors[parseInt(id) - 1]["commissionIncome"] != undefined && newAmount.trim() != "") {
            let newEmpErrors = [...empErrors];
            delete newEmpErrors[parseInt(id) - 1]["commissionIncome"];
            setEmpErrors(newEmpErrors);
        }
    };

    const setCommissionIncomeFrequency = (id: string, newFrequency: string) => {
        currentEmployment.forEach(c => {
            if (c.displayId == id) {
                let newEmploymentInfo: currentEmploymentInfo = { ...c }
                newEmploymentInfo.commissionIncomeFrequency = newFrequency;
                dispatch(setCurrentEmploymentInfo(newEmploymentInfo));
            }
        });
        if (empErrors[parseInt(id) - 1]["commissionIncomeFrequency"] != undefined && newFrequency.trim() != "") {
            let newEmpErrors = [...empErrors];
            delete newEmpErrors[parseInt(id) - 1]["commissionIncomeFrequency"];
            setEmpErrors(newEmpErrors);
        }
    };

    const setTipsSelected = (id: string, tipsSelected: boolean) => {
        currentEmployment.forEach(c => {
            if (c.displayId == id) {
                let newEmploymentInfo: currentEmploymentInfo = { ...c }
                newEmploymentInfo.hasTipsIncome = tipsSelected;
                if (!tipsSelected) {
                    newEmploymentInfo.tipsIncome = 0;
                    newEmploymentInfo.tipsIncomeText = "";
                    newEmploymentInfo.tipsIncomeFrequency = "";
                }
                dispatch(setCurrentEmploymentInfo(newEmploymentInfo));
            }
        });
    };

    const setTipsIncomeAmount = (id: string, newAmount: string) => {
        currentEmployment.forEach(c => {
            if (c.displayId == id) {
                let newEmploymentInfo: currentEmploymentInfo = { ...c }
                newEmploymentInfo.tipsIncomeText = newAmount;
                newEmploymentInfo.tipsIncome = +newAmount;
                dispatch(setCurrentEmploymentInfo(newEmploymentInfo));
            }
        });
        if (empErrors[parseInt(id) - 1]["tipsIncome"] != undefined && newAmount.trim() != "") {
            let newEmpErrors = [...empErrors];
            delete newEmpErrors[parseInt(id) - 1]["tipsIncome"];
            setEmpErrors(newEmpErrors);
        }
    };

    const setTipsIncomeFrequency = (id: string, newFrequency: string) => {
        currentEmployment.forEach(c => {
            if (c.displayId == id) {
                let newEmploymentInfo: currentEmploymentInfo = { ...c }
                newEmploymentInfo.tipsIncomeFrequency = newFrequency;
                dispatch(setCurrentEmploymentInfo(newEmploymentInfo));
            }
        });
        if (empErrors[parseInt(id) - 1]["tipsIncomeFrequency"] != undefined && newFrequency.trim() != "") {
            let newEmpErrors = [...empErrors];
            delete newEmpErrors[parseInt(id) - 1]["tipsIncomeFrequency"];
            setEmpErrors(newEmpErrors);
        }
    };

    const setEmployerName = (id: string, newName: string) => {
        currentEmployment.forEach(c => {
            if (c.displayId == id) {
                let newEmploymentInfo: currentEmploymentInfo = { ...c }
                newEmploymentInfo.employerName = newName;
                dispatch(setCurrentEmploymentInfo(newEmploymentInfo));
            }
        });
        if (empErrors[parseInt(id) - 1]["employerName"] != undefined && newName.trim() != "") {
            let newEmpErrors = [...empErrors];
            delete newEmpErrors[parseInt(id) - 1]["employerName"];
            setEmpErrors(newEmpErrors);
        }
    };

    const setEmployerPhone = (id: string, newPhone: string) => {
        currentEmployment.forEach(c => {
            if (c.displayId == id) {
                let newEmploymentInfo: currentEmploymentInfo = { ...c }
                newEmploymentInfo.employerPhone = newPhone;
                dispatch(setCurrentEmploymentInfo(newEmploymentInfo));

                if (empErrors[parseInt(id) - 1]["employerPhoneNumber"] != undefined && newPhone.trim() != "") {
                    let newEmpErrors = [...empErrors];
                    delete newEmpErrors[parseInt(id) - 1]["employerPhoneNumber"];
                    setEmpErrors(newEmpErrors);
                }
            }
        });
    };

    const setEmployerAddress = (id: string, newAddress: employerAddressInfo) => {
        currentEmployment.forEach(c => {
            if (c.displayId == id) {
                let newEmploymentInfo: currentEmploymentInfo = { ...c }
                newEmploymentInfo.employerAddress = newAddress;
                dispatch(setCurrentEmploymentInfo(newEmploymentInfo));
            }
        });
    };

    const setProbationExists = (id: string, response: string) => {
        currentEmployment.forEach(c => {
            if (c.displayId == id) {
                let newEmploymentInfo: currentEmploymentInfo = { ...c }
                newEmploymentInfo.isProbation = response;
                console.log("setProbationExists", newEmploymentInfo);
                dispatch(setCurrentEmploymentInfo(newEmploymentInfo));
            }
        });
        if (empErrors[parseInt(id) - 1]["isProbation"] != undefined && response.trim() != "") {
            let newEmpErrors = [...empErrors];
            delete newEmpErrors[parseInt(id) - 1]["isProbation"];
            setEmpErrors(newEmpErrors);
        }
    };

    const setFamilyOwned = (id: string, response: string) => {
        currentEmployment.forEach(c => {
            if (c.displayId == id) {
                let newEmploymentInfo: currentEmploymentInfo = { ...c }
                newEmploymentInfo.isFamilyOwned = response;
                dispatch(setCurrentEmploymentInfo(newEmploymentInfo));
            }
        });
        if (empErrors[parseInt(id) - 1]["isFamilyOwned"] != undefined && response.trim() != "") {
            let newEmpErrors = [...empErrors];
            delete newEmpErrors[parseInt(id) - 1]["isFamilyOwned"];
            setEmpErrors(newEmpErrors);
        }
    };

    const setOnLeave = (id: string, response: string) => {
        currentEmployment.forEach(c => {
            if (c.displayId == id) {
                let newEmploymentInfo: currentEmploymentInfo = { ...c }
                newEmploymentInfo.isOnLeave = response;
                if (response === "no") {
                    newEmploymentInfo.leaveType = "";
                    newEmploymentInfo.workReturnDate = "";
                }
                dispatch(setCurrentEmploymentInfo(newEmploymentInfo));
            }
        });
        if (empErrors[parseInt(id) - 1]["isOnLeave"] != undefined && response.trim() != "") {
            let newEmpErrors = [...empErrors];
            delete newEmpErrors[parseInt(id) - 1]["isOnLeave"];
            setEmpErrors(newEmpErrors);
        }
    };

    const setLeaveType = (id: string, response: string) => {
        currentEmployment.forEach(c => {
            if (c.displayId == id) {
                let newEmploymentInfo: currentEmploymentInfo = { ...c }
                newEmploymentInfo.leaveType = response;
                if (response === "parental") {
                    newEmploymentInfo.otherLeaveDescription = "";
                }
                dispatch(setCurrentEmploymentInfo(newEmploymentInfo));
            }
        });
        if (empErrors[parseInt(id) - 1]["leaveType"] != undefined && response.trim() != "") {
            let newEmpErrors = [...empErrors];
            delete newEmpErrors[parseInt(id) - 1]["leaveType"];
            setEmpErrors(newEmpErrors);
        }
    };

    const setLeaveDescription = (id: string, description: string) => {
        currentEmployment.forEach(c => {
            if (c.displayId == id) {
                let newEmploymentInfo: currentEmploymentInfo = { ...c }
                newEmploymentInfo.otherLeaveDescription = description;
                dispatch(setCurrentEmploymentInfo(newEmploymentInfo));
            }
        });
        if (empErrors[parseInt(id) - 1]["otherLeaveDesc"] != undefined && description.trim() != "") {
            let newEmpErrors = [...empErrors];
            delete newEmpErrors[parseInt(id) - 1]["otherLeaveDesc"];
            setEmpErrors(newEmpErrors);
        }
    };

    const setWorkReturnDate = (id: string, newDate: string) => {
        currentEmployment.forEach(c => {
            if (c.displayId == id) {
                let newEmploymentInfo: currentEmploymentInfo = { ...c }
                newEmploymentInfo.workReturnDate = newDate;
                dispatch(setCurrentEmploymentInfo(newEmploymentInfo));
            }
        });
        if (empErrors[parseInt(id) - 1]["workReturnDate"] != undefined && newDate.trim() != "") {
            let newEmpErrors = [...empErrors];
            delete newEmpErrors[parseInt(id) - 1]["workReturnDate"];
            setEmpErrors(newEmpErrors);
        }
    };

    const confirmDeletion = (id: string) => {
        confirmDialog({
            message: <p style={{ marginLeft: -14 }}>Are you sure you would like to remove this employment?</p>,
            header: <h2 className="flex"><img src="assets/error-icon.svg" style={{ width: 24, marginRight: 12 }} />Remove Employment?</h2>,
            defaultFocus: 'reject',
            acceptClassName: style.confirm,
            acceptLabel: "Remove Employment",
            rejectClassName: style.cancel,
            rejectLabel: "Cancel",
            accept: () => {
                //Remove Employment
                dispatch(removeCurrentEmployment(id));
            },
            draggable: false,
            closable: false
        });
    };

    const currentEmploymentList = () => {
        return (
            <AnimatePresence>
                {currentEmployment.map((position: any) => (
                    <motion.div
                        key={position.displayId}
                        initial={{ y: -100, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ type: "linear" }}
                    >
                        <div className="flex flex-row justify-between mt-5">
                            <label htmlFor="" className="cursor-pointer"
                                style={{ alignContent: "center", fontSize: 20, textWrap: "nowrap" }}
                                onClick={() => { toggleEmployerVisibility(position.displayId) }}>Current Employer {position.displayId}</label>
                            <div className="cursor-pointer" style={{ width: "100%" }} onClick={() => { toggleEmployerVisibility(position.displayId) }}></div>
                            <div className="flex flex-row">
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (position.status != "") {
                                            confirmDeletion(position.displayId);
                                        } else {
                                            dispatch(removeCurrentEmployment(position.displayId));
                                        }
                                    }}
                                    className="secondary sm:w-28 h-11 px-6 ml-3"
                                    style={{ color: 'var(--brand-blue)', background: "transparent", width: 125, order: 0 }}>
                                    <img
                                        className="mr-3"
                                        src="assets/remove-item.svg"
                                        alt="tooltip"
                                        width={20}
                                        height={20}
                                    />
                                    Remove
                                </button>
                                <img
                                    onClick={() => { toggleEmployerVisibility(position.displayId) }}
                                    src={position.visible ? "assets/chevron-down.svg" : "assets/chevron-up.svg"}
                                    className={clsx(
                                        "w-5 h-6 top-6 ml-3"
                                    )}
                                    alt="error"
                                />
                            </div>
                        </div>

                        {position.visible &&
                            <>
                                <div className="flex md:flex-row flex-col">
                                    <div className={clsx(style.twoColFirst, "mt-5")}>
                                        <label htmlFor="employmentStatus">Employment status</label>
                                        <div className="flex relative">
                                            <span>
                                                <img
                                                    src="assets/error-icon.svg"
                                                    className={clsx(
                                                        empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["employmentStatus"] != undefined
                                                            ? ""
                                                            : "hidden",
                                                        "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                    )}
                                                    alt="error"
                                                    height={5}
                                                    width={5}
                                                />
                                            </span>
                                            <select
                                                id="employmentStatus"
                                                onChange={(e) => {
                                                    setEmploymentStatus(position.displayId, e.target.value)
                                                    if (e.target.value === "unemployed" || e.target.value === "retired") {
                                                        clearEmploymentFields(position.id, position.displayId, e.target.value);
                                                    }
                                                }}
                                                value={position.status}
                                                className={clsx(
                                                    empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["employmentStatus"] != undefined
                                                        ? "error"
                                                        : "",
                                                    "mt-2 w:[220px] md:w-[305px]"
                                                )}
                                                style={{ alignItems: 'center' }}
                                            >
                                                <option hidden value="">
                                                    Choose...
                                                </option>
                                                <option value="employed">Employed</option>
                                                <option value="selfemployed">Self Employed</option>
                                                {(retirementAvailable || position.status.trim() === "retired" || position.status.trim() === "") && <option value="retired">Retired</option>}
                                                {(unemployedAvailable || position.status.trim() === "unemployed" || position.status.trim() === "") && <option value="unemployed">Not Employed</option>}
                                            </select>
                                        </div>
                                        {empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["employmentStatus"] != undefined && (
                                            <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                {empErrors[position.displayId - 1]["employmentStatus"]}
                                            </span>
                                        )}
                                    </div>
                                    {(position?.status === "employed" || position?.status === "selfemployed") &&
                                        <>
                                            <div className="mt-5">
                                                <label htmlFor="employmentType">Employment type</label>
                                                <div className="flex relative">
                                                    <span>
                                                        <img
                                                            src="assets/error-icon.svg"
                                                            className={clsx(
                                                                empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["employmentType"] != undefined
                                                                    ? ""
                                                                    : "hidden",
                                                                "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                            )}
                                                            alt="error"
                                                            height={5}
                                                            width={5}
                                                        />
                                                    </span>
                                                    <select
                                                        id="employmentType"
                                                        onChange={(e) => {
                                                            setEmploymentType(position.displayId, e.target.value)
                                                        }}
                                                        value={position.type}
                                                        className={clsx(
                                                            empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["employmentType"] != undefined
                                                                ? "error"
                                                                : "",
                                                            "mt-2 w:[220px] md:w-[305px]"
                                                        )}
                                                        style={{ alignItems: 'center' }}
                                                    >
                                                        <option hidden value="">
                                                            Choose...
                                                        </option>
                                                        <option value="fulltime">Full-time</option>
                                                        <option value="parttime">Part-time</option>
                                                        <option value="seasonalfullparttime">Seasonal Full/Part Time</option>
                                                        <option value="contract">Contract</option>
                                                        <option value="selfemployed">Self Employed</option>
                                                    </select>
                                                </div>
                                                {empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["employmentType"] != undefined && (
                                                    <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                        {empErrors[position.displayId - 1]["employmentType"]}
                                                    </span>
                                                )}
                                            </div>
                                        </>
                                    }
                                    {(position?.status == "unemployed") &&
                                        <div className="mt-5">
                                            <label htmlFor="jobTitle">How long have you been not employed?</label>
                                            <div className="flex relative">
                                                <span>
                                                    <img
                                                        src="assets/error-icon.svg"
                                                        className={clsx(
                                                            "hidden",
                                                            "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                                                        )}
                                                        alt="error"
                                                        height={5}
                                                        width={5}
                                                    />
                                                </span>
                                                <input
                                                    className={clsx(
                                                        empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["notWorkingYears"] != undefined ?
                                                            "mt-2 error" : "mt-2", style.residentPeriod
                                                    )}
                                                    type="number"
                                                    min={0}
                                                    placeholder="year(s)"
                                                    max={99}
                                                    id={"notWorkingYears" + position.displayId}
                                                    value={position.notWorkingYears != undefined ? position.notWorkingYears : ""}
                                                    onChange={(e) => {
                                                        if (e.target.value.charAt(0) == "0") {
                                                            e.target.value = e.target.value.slice(1, 2);
                                                        } else {
                                                            e.target.value = e.target.value.slice(0, 2);
                                                        }
                                                        setNotWorkingLength(position.displayId, e.target.value)
                                                    }}
                                                />
                                                {position.notWorkingYears != undefined &&
                                                    <span style={{ position: "absolute", top: "19px", left: position.notWorkingYears.toString().length > 1 ? "28px" : "22px" }}>year{position.notWorkingYears != 1 ? "s" : ""}</span>
                                                }
                                                <input
                                                    className={clsx(
                                                        empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["notWorkingMonths"] != undefined ?
                                                            "mt-2 ml-2 error" : "mt-2 ml-2", style.residentPeriod
                                                    )}
                                                    type="number"
                                                    min={0}
                                                    max={11}
                                                    placeholder="month(s)"
                                                    id={"notWorkingMonths" + position.displayId}
                                                    value={position.notWorkingMonths != undefined ? position.notWorkingMonths : ""}
                                                    onChange={(e) => {
                                                        if (e.target.value.charAt(0) == "0") {
                                                            e.target.value = e.target.value.slice(1, 2);
                                                        } else {
                                                            e.target.value = e.target.value.slice(0, 2);
                                                        }
                                                        if (+e.target.value > 11) {
                                                            e.target.value = "11"
                                                        }
                                                        setNotWorkingLength(position.displayId, undefined, e.target.value)
                                                    }}
                                                />
                                                {position.notWorkingMonths != undefined &&
                                                    <span style={{ position: "absolute", top: "19px", left: position.notWorkingMonths.toString().length > 1 ? "185px" : "180px" }}>month{position.notWorkingMonths != 1 ? "s" : ""}</span>
                                                }
                                            </div>
                                            {((empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["notWorkingYears"] != undefined) || (empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["notWorkingMonths"] != undefined)) && (
                                                <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                    Enter your time unemployed.
                                                </span>
                                            )}
                                        </div>
                                    }
                                    {(position?.status == "retired") &&
                                        <div className="mt-5">
                                            <label htmlFor="jobTitle">How long have you been retired?</label>
                                            <div className="flex relative">
                                                <span>
                                                    <img
                                                        src="assets/error-icon.svg"
                                                        className={clsx(
                                                            "hidden",
                                                            "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                                                        )}
                                                        alt="error"
                                                        height={5}
                                                        width={5}
                                                    />
                                                </span>
                                                <input
                                                    className={clsx(
                                                        empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["notWorkingYears"] != undefined ?
                                                            "mt-2 error" : "mt-2", style.residentPeriod
                                                    )}
                                                    type="number"
                                                    min={0}
                                                    placeholder="year(s)"
                                                    max={99}
                                                    id={"notWorkingYears" + position.displayId}
                                                    value={position.notWorkingYears != undefined ? position.notWorkingYears : ""}
                                                    onChange={(e) => {
                                                        if (e.target.value.charAt(0) == "0") {
                                                            e.target.value = e.target.value.slice(1, 2);
                                                        } else {
                                                            e.target.value = e.target.value.slice(0, 2);
                                                        }
                                                        setNotWorkingLength(position.displayId, e.target.value)
                                                    }}
                                                />
                                                {position.notWorkingYears != undefined &&
                                                    <span style={{ position: "absolute", top: "19px", left: position.notWorkingYears.toString().length > 1 ? "28px" : "22px" }}>year{position.notWorkingYears != 1 ? "s" : ""}</span>
                                                }
                                                <input
                                                    className={clsx(
                                                        empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["notWorkingYears"] != undefined ?
                                                            "mt-2 ml-2 error" : "mt-2 ml-2", style.residentPeriod
                                                    )}
                                                    type="number"
                                                    min={0}
                                                    max={11}
                                                    placeholder="month(s)"
                                                    id={"notWorkingMonths" + position.displayId}
                                                    value={position.notWorkingMonths != undefined ? position.notWorkingMonths : ""}
                                                    onChange={(e) => {
                                                        if (e.target.value.charAt(0) == "0") {
                                                            e.target.value = e.target.value.slice(1, 2);
                                                        } else {
                                                            e.target.value = e.target.value.slice(0, 2);
                                                        }
                                                        if (+e.target.value > 11) {
                                                            e.target.value = "11"
                                                        }
                                                        setNotWorkingLength(position.displayId, undefined, e.target.value)
                                                    }}
                                                />
                                                {position.notWorkingMonths != undefined &&
                                                    <span style={{ position: "absolute", top: "19px", left: position.notWorkingMonths.toString().length > 1 ? "185px" : "180px" }}>month{position.notWorkingMonths != 1 ? "s" : ""}</span>
                                                }
                                            </div>
                                            {((empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["notWorkingYears"] != undefined) || (empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["notWorkingMonths"] != undefined)) && (
                                                <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                    Enter your time in retirement.
                                                </span>
                                            )}
                                        </div>
                                    }
                                </div>

                                {(position?.status === "employed" || position?.status === "selfemployed") &&
                                    <>
                                        <div className="flex md:flex-row flex-col">
                                            <div className={style.twoColFirst}>
                                                <div className={clsx(
                                                    empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["occupation"] != undefined
                                                        ? "error"
                                                        : "",
                                                    "mt-5 w:[220px] md:w-[305px]"
                                                )}>
                                                    <label htmlFor="occupation">Occupation</label>
                                                    <div className="flex relative">
                                                        <span>
                                                            <SearchableDropdown
                                                                options={sessionData.occupations}
                                                                label="name"
                                                                id={"occupation" + position.displayId}
                                                                selectedVal={position.occupation}
                                                                handleChange={(val: any) => {
                                                                    let category = sessionData.occupations?.find((x) => x.name == val)?.category;
                                                                    setOccupation(position.displayId, val, category ? category : "");
                                                                }}
                                                                customStyle={{ paddingRight: 40 }}
                                                                disableForm={true}
                                                                fetching={false}
                                                                displayError={false}
                                                            />
                                                        </span>
                                                    </div>
                                                    <small
                                                        className={clsx(
                                                            methods.formState?.errors?.occupation ? "hidden" : "",
                                                            "text-gray-400"
                                                        )}
                                                    >
                                                        <b>Example</b>: Financial Planner, Marketing Manager
                                                    </small>
                                                    {empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["occupation"] != undefined && (
                                                        <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                            {empErrors[position.displayId - 1]["occupation"]}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="mt-5">
                                                <label htmlFor="jobTitle">Job title</label>
                                                <div className="flex relative">
                                                    <span>
                                                        <img
                                                            src="assets/error-icon.svg"
                                                            className={clsx(
                                                                empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["jobTitle"]
                                                                    ? ""
                                                                    : "hidden",
                                                                "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                            )}
                                                            alt="error"
                                                            height={5}
                                                            width={5}
                                                        />
                                                    </span>
                                                    <input
                                                        className={clsx(
                                                            empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["jobTitle"] != undefined
                                                                ? "error"
                                                                : "",
                                                            "mt-2 w:[220px] md:w-[305px]"
                                                        )}
                                                        type="text"
                                                        id={"jobTitle" + position.displayId}
                                                        onChange={(e) => {
                                                            setJobTitle(position.displayId, e.target.value)
                                                        }}
                                                        value={position.jobTitle}
                                                        maxLength={30}
                                                    />
                                                </div>
                                                {empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["jobTitle"] != undefined && (
                                                    <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                        {empErrors[position.displayId - 1]["jobTitle"]}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        {(position?.status === "selfemployed") &&
                                            <div className="flex md:flex-row flex-col">
                                                <div className={clsx(style.twoColFirst, "mt-5")}>
                                                    <label htmlFor="ownership">Type of Ownership</label>
                                                    <div className="flex relative">
                                                        <span>
                                                            <img
                                                                src="assets/error-icon.svg"
                                                                className={clsx(
                                                                    empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["ownership"] != undefined
                                                                        ? ""
                                                                        : "hidden",
                                                                    "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                                )}
                                                                alt="error"
                                                                height={5}
                                                                width={5}
                                                            />
                                                        </span>
                                                        <select
                                                            id="ownership"
                                                            onChange={(e) => {
                                                                setOwnership(position.displayId, e.target.value)
                                                            }}
                                                            value={position.ownershipType}
                                                            className={clsx(
                                                                empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["ownership"] != undefined
                                                                    ? "error"
                                                                    : "",
                                                                "mt-2 w:[220px] md:w-[305px]"
                                                            )}
                                                            style={{ alignItems: 'center' }}
                                                        >
                                                            <option hidden value="">
                                                                Choose...
                                                            </option>
                                                            <option value="soleproprietor">Sole Proprietorship</option>
                                                            <option value="partnership">Partnership</option>
                                                            <option value="corporation">Corporation</option>
                                                        </select>
                                                    </div>
                                                    {empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["ownership"] != undefined && (
                                                        <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                            {empErrors[position.displayId - 1]["ownership"]}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="mt-5">
                                                    <label htmlFor="jobTitle">Business Type</label>
                                                    <div className="flex relative">
                                                        <span>
                                                            <img
                                                                src="assets/error-icon.svg"
                                                                className={clsx(
                                                                    empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["businessType"] != undefined
                                                                        ? ""
                                                                        : "hidden",
                                                                    "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                                )}
                                                                alt="error"
                                                                height={5}
                                                                width={5}
                                                            />
                                                        </span>
                                                        <input
                                                            className={clsx(
                                                                empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["businessType"] != undefined
                                                                    ? "error"
                                                                    : "",
                                                                "mt-2 w:[220px] md:w-[305px]"
                                                            )}
                                                            type="text"
                                                            id={"businessType" + position.displayId}
                                                            onChange={(e) => {
                                                                setBusinessType(position.displayId, e.target.value)
                                                            }}
                                                            value={position.businessType}
                                                            maxLength={30}
                                                        />
                                                    </div>
                                                    {empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["businessType"] != undefined && (
                                                        <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                            {empErrors[position.displayId - 1]["businessType"]}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        }
                                        <div className="flex md:flex-row flex-col">
                                            <div className={clsx(style.twoColFirst, "mt-5")}>
                                                <label htmlFor="jobTitle">Time in industry</label>
                                                <div className="flex relative">
                                                    <span>
                                                        <img
                                                            src="assets/error-icon.svg"
                                                            className={clsx(
                                                                "hidden",
                                                                "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                                                            )}
                                                            alt="error"
                                                            height={5}
                                                            width={5}
                                                        />
                                                    </span>
                                                    <input
                                                        className={clsx(
                                                            empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["industryLengthYears"] != undefined
                                                                ? "mt-2 error"
                                                                : "mt-2",
                                                            style.residentPeriod
                                                        )}
                                                        type="number"
                                                        min={0}
                                                        placeholder="year(s)"
                                                        max={99}
                                                        id={"industryLengthYears" + position.displayId}
                                                        value={position.industryYears != undefined ? position.industryYears : ""}
                                                        onChange={(e) => {
                                                            if (e.target.value.charAt(0) == "0") {
                                                                e.target.value = e.target.value.slice(1, 2);
                                                            } else {
                                                                e.target.value = e.target.value.slice(0, 2);
                                                            }
                                                            setIndustryLength(position.displayId, e.target.value)
                                                        }}
                                                    />
                                                    {position.industryYears != undefined &&
                                                        <span style={{ position: "absolute", top: "19px", left: position.industryYears.toString().length > 1 ? "28px" : "22px" }}>year{position.industryYears != 1 ? "s" : ""}</span>
                                                    }
                                                    <input
                                                        className={clsx(
                                                            empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["industryLengthMonths"] != undefined
                                                                ? "mt-2 ml-2 error"
                                                                : "mt-2 ml-2",
                                                            style.residentPeriod
                                                        )}
                                                        type="number"
                                                        min={0}
                                                        max={11}
                                                        placeholder="month(s)"
                                                        id={"industryLengthMonths" + position.displayId}
                                                        value={position.industryMonths != undefined ? position.industryMonths : ""}
                                                        onChange={(e) => {
                                                            if (e.target.value.charAt(0) == "0") {
                                                                e.target.value = e.target.value.slice(1, 2);
                                                            } else {
                                                                e.target.value = e.target.value.slice(0, 2);
                                                            }
                                                            if (+e.target.value > 11) {
                                                                e.target.value = "11"
                                                            }
                                                            setIndustryLength(position.displayId, undefined, e.target.value)
                                                        }}
                                                    />
                                                    {position.industryMonths != undefined &&
                                                        <span style={{ position: "absolute", top: "19px", left: position.industryMonths.toString().length > 1 ? "185px" : "180px" }}>month{position.industryMonths != 1 ? "s" : ""}</span>
                                                    }
                                                </div>
                                                {((empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["industryLengthYears"] != undefined) || (empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["industryLengthMonths"] != undefined)) && (
                                                    <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                        Enter your time at this industry.
                                                    </span>
                                                )}
                                            </div>
                                            <div className="mt-5">
                                                <label htmlFor="jobTitle">Duration of employment</label>
                                                <div className="flex relative">
                                                    <span>
                                                        <img
                                                            src="assets/error-icon.svg"
                                                            className={clsx(
                                                                "hidden",
                                                                "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                                                            )}
                                                            alt="error"
                                                            height={5}
                                                            width={5}
                                                        />
                                                    </span>
                                                    <input
                                                        className={clsx(
                                                            empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["employmentYears"] != undefined ?
                                                                "mt-2 error" : "mt-2", style.residentPeriod
                                                        )}
                                                        type="number"
                                                        min={0}
                                                        placeholder="year(s)"
                                                        max={99}
                                                        id={"employmentLengthYears" + position.displayId}
                                                        value={position.employmentYears != undefined ? position.employmentYears : ""}
                                                        onChange={(e) => {
                                                            if (e.target.value.charAt(0) == "0") {
                                                                e.target.value = e.target.value.slice(1, 2);
                                                            } else {
                                                                e.target.value = e.target.value.slice(0, 2);
                                                            }
                                                            setEmploymentLength(position.displayId, e.target.value)
                                                        }}
                                                    />
                                                    {position.employmentYears != undefined &&
                                                        <span style={{ position: "absolute", top: "19px", left: position.employmentYears.toString().length > 1 ? "28px" : "22px" }}>year{position.employmentYears != 1 ? "s" : ""}</span>
                                                    }
                                                    <input
                                                        className={clsx(
                                                            empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["employmentMonths"] != undefined ?
                                                                "mt-2 ml-2 error" : "mt-2 ml-2", style.residentPeriod
                                                        )}
                                                        type="number"
                                                        min={0}
                                                        max={11}
                                                        placeholder="month(s)"
                                                        id={"employmentLengthMonths" + position.displayId}
                                                        value={position.employmentMonths != undefined ? position.employmentMonths : ""}
                                                        onChange={(e) => {
                                                            if (e.target.value.charAt(0) == "0") {
                                                                e.target.value = e.target.value.slice(1, 2);
                                                            } else {
                                                                e.target.value = e.target.value.slice(0, 2);
                                                            }
                                                            if (+e.target.value > 11) {
                                                                e.target.value = "11"
                                                            }
                                                            setEmploymentLength(position.displayId, undefined, e.target.value)
                                                        }}
                                                    />
                                                    {position.employmentMonths != undefined &&
                                                        <span style={{ position: "absolute", top: "19px", left: position.employmentMonths.toString().length > 1 ? "185px" : "180px" }}>month{position.employmentMonths != 1 ? "s" : ""}</span>
                                                    }
                                                </div>
                                                {((empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["employmentYears"] != undefined) || (empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["employmentMonths"] != undefined)) && (
                                                    <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                        Enter your time with this employer.
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {(position?.status === "selfemployed") &&
                                            <>
                                                <div
                                                    className={clsx(
                                                        "flex mt-5"
                                                    )}
                                                >
                                                    <span className="mr-1">
                                                        <label htmlFor={position.displayId + "businessLendingFromMCU"}>Does the business have lending products with Meridian?</label>
                                                    </span>
                                                </div>
                                                <div className="mt-3">
                                                    <ul className="flex">
                                                        <li className="relative mr-5">
                                                            <input
                                                                id={position.displayId + "businessLendingFromMCUYes"}
                                                                type="radio"
                                                                value="yes"
                                                                className="sr-only peer"
                                                                checked={position.hasBusinessLending === "yes"}
                                                                onChange={(e) => {
                                                                    setBusinessLending(position.displayId, e.target.value)
                                                                }}
                                                            />
                                                            <label
                                                                className={clsx(
                                                                    style.smTypeLabel,
                                                                    "cursor-pointer focus:outline-none hover:bg-gray-100  text-sm text-center peer-checked:ring-[--brand-blue] peer-checked:ring-2 peer-checked:border-transparent"
                                                                )}
                                                                htmlFor={position.displayId + "businessLendingFromMCUYes"}
                                                            >
                                                                <p className="p-2 brand-blue-text">Yes</p>
                                                            </label>
                                                        </li>
                                                        <li className="relative">
                                                            <input
                                                                id={position.displayId + "businessLendingFromMCUNo"}
                                                                type="radio"
                                                                value="no"
                                                                className="sr-only peer"
                                                                checked={position.hasBusinessLending === "no"}
                                                                onChange={(e) => {
                                                                    setBusinessLending(position.displayId, e.target.value)
                                                                }}
                                                            />
                                                            <label
                                                                className={clsx(
                                                                    style.smTypeLabel,
                                                                    "cursor-pointer focus:outline-none hover:bg-gray-100 text-sm text-center peer-checked:ring-[--brand-blue] peer-checked:ring-2 peer-checked:border-transparent"
                                                                )}
                                                                htmlFor={position.displayId + "businessLendingFromMCUNo"}
                                                            >
                                                                <p className="p-2 brand-blue-text">No</p>
                                                            </label>
                                                        </li>
                                                    </ul>
                                                    {empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["businessLendingFromMCU"] != undefined && (
                                                        <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                            {empErrors[position.displayId - 1]["businessLendingFromMCU"]}
                                                        </span>
                                                    )}
                                                </div>
                                            </>
                                        }
                                    </>
                                }

                                <hr className="mt-5" />

                                {(position?.status !== "unemployed" && position?.status.trim() !== "") &&
                                    <>
                                        {/* {position?.status === "retired" &&
                                            <div className="flex flex-row">
                                                <div className="mt-5 flex flex-col-reverse">
                                                    <label></label>
                                                    <input
                                                        id={position.displayId + "noRetirementIncomePrompt"}
                                                        type="checkbox"
                                                        checked={position.noRetirementIncome}
                                                        onChange={(e) => setNoRetirementIncome(position.displayId, e.target.checked)}
                                                        className="accent"
                                                    />
                                                    <label htmlFor="noRetirementIncomePrompt">
                                                        <p className="sub-heading ml-7 -mt-5">
                                                            I have no retirement income
                                                        </p>
                                                    </label>
                                                </div>
                                            </div>
                                        } */}
                                        {(position?.status == "employed" || position?.status == "selfemployed") &&
                                            <>
                                                <div className="flex md:flex-row flex-col mt-5">
                                                    <div className="col-span-1">
                                                        <label htmlFor="payrollType">Payment Type</label>
                                                        <div className="flex relative">
                                                            <span>
                                                                <img
                                                                    src="assets/error-icon.svg"
                                                                    className={clsx(
                                                                        empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["payrollType"] != undefined
                                                                            ? ""
                                                                            : "hidden",
                                                                        "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                                    )}
                                                                    alt="error"
                                                                    height={5}
                                                                    width={5}
                                                                />
                                                            </span>
                                                            <select
                                                                id="payrollType"
                                                                value={position.payrollType}
                                                                onChange={(e) => {
                                                                    setPayrollType(position.displayId, e.target.value)
                                                                }}
                                                                className={clsx(
                                                                    empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["payrollType"] != undefined
                                                                        ? "error"
                                                                        : "",
                                                                    "mt-2 w:[220px] md:w-[305px]"
                                                                )}
                                                                style={{ alignItems: 'center' }}
                                                            >
                                                                <option hidden value="">
                                                                    Choose...
                                                                </option>
                                                                {(position?.type != "selfemployed" && position?.status != "selfemployed" && position?.status != "retired") && (
                                                                    <>
                                                                        <option value="salary">Salary</option>
                                                                        <option value="commission">Commission Only</option>
                                                                        <option value="hourlyguaranteed">Hourly (Guaranteed)</option>
                                                                        <option value="hourlynonguaranteed">Hourly (Non-Guaranteed)</option>
                                                                    </>)}

                                                                {((position?.type == "selfemployed" || position?.status == "selfemployed") && position?.status != "retired") && (
                                                                    <>
                                                                        <option value="business">Net Income (for Sole-Proprietor/Partnership)</option>
                                                                        <option value="commission">Commission</option>
                                                                        <option value="dividends">Dividends</option>
                                                                        <option value="salary">Salary</option>
                                                                    </>
                                                                )}
                                                                {(position?.type != "selfemployed" && position?.status != "selfemployed" && position?.status == "retired") && (
                                                                    <>
                                                                        <option value="cpp">CPP</option>
                                                                        <option value="oas">OAS</option>
                                                                        <option value="employerpension">Employer Pension</option>
                                                                        <option value="permanentdisability">Permanent Disability</option>
                                                                        <option value="rrif">RRIF</option>
                                                                    </>
                                                                )}
                                                            </select>
                                                        </div>
                                                        {empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["payrollType"] != undefined && (
                                                            <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                                {empErrors[position.displayId - 1]["payrollType"]}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="flex md:flex-row flex-col">
                                                    <div className={clsx(style.twoColFirst, "mt-5")}>
                                                        <label htmlFor={position.displayId + "employmentIncome"}>Income</label>
                                                        <div className="flex relative">
                                                            <span>
                                                                <img
                                                                    src="assets/error-icon.svg"
                                                                    className={clsx(
                                                                        empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["income"] != undefined
                                                                            ? ""
                                                                            : "hidden",
                                                                        "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                                    )}
                                                                    alt="error"
                                                                    height={5}
                                                                    width={5}
                                                                />
                                                            </span>
                                                            <CurrencyInput
                                                                id={position.displayId + "employmentIncome"}
                                                                placeholder="$"
                                                                prefix={"$"}
                                                                decimalsLimit={2}
                                                                decimalScale={2}
                                                                allowNegativeValue={false}
                                                                maxLength={9}
                                                                value={position.incomeText}
                                                                className={clsx(
                                                                    empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["income"] != undefined
                                                                        ? "error"
                                                                        : "",
                                                                    "mt-2 w:[220px] md:w-[305px]"
                                                                )}
                                                                onValueChange={(value, name, values) => value != undefined ? setIncomeAmount(position.displayId, value) : setIncomeAmount(position.displayId, "")}
                                                            />
                                                        </div>
                                                        {empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["income"] != undefined && (
                                                            <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                                {empErrors[position.displayId - 1]["income"]}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="mt-5">
                                                        <label htmlFor={position.displayId + "payrollFrequency"}>Frequency</label>
                                                        <div className="flex relative">
                                                            <span>
                                                                <img
                                                                    src="assets/error-icon.svg"
                                                                    className={clsx(
                                                                        empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["payrollFrequency"] != undefined
                                                                            ? ""
                                                                            : "hidden",
                                                                        "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                                    )}
                                                                    alt="error"
                                                                    height={5}
                                                                    width={5}
                                                                />
                                                            </span>
                                                            <select
                                                                id={position.displayId + "payrollFrequency"}
                                                                value={position.payrollFrequency}
                                                                onChange={(e) => {
                                                                    setPayrollFrequency(position.displayId, e.target.value)
                                                                }}
                                                                className={clsx(
                                                                    empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["payrollFrequency"] != undefined
                                                                        ? "error"
                                                                        : "",
                                                                    "mt-2 w:[220px] md:w-[305px]"
                                                                )}
                                                                style={{ alignItems: 'center' }}
                                                            >
                                                                <option hidden value="">
                                                                    Choose...
                                                                </option>
                                                                <option value="weekly">Weekly</option>
                                                                <option value="biweekly">Bi-Weekly</option>
                                                                <option value="monthly">Monthly</option>
                                                                <option value="semimonthly">Semi-Monthly</option>
                                                                <option value="annually">Annually</option>
                                                            </select>
                                                        </div>
                                                        {empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["payrollFrequency"] != undefined && (
                                                            <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                                {empErrors[position.displayId - 1]["payrollFrequency"]}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </>
                                        }

                                        {/** Other Income from Retired Status */}
                                        {position?.status === "retired" &&
                                            <>
                                                {position?.otherIncome.map((income: any) => (
                                                    <div key={"otherIncome-" + income.displayId} className="flex md:flex-row flex-col mt-5">
                                                        <div className={style.twoColFirst}>
                                                            <label htmlFor="payrollType">Income Type</label>
                                                            <div className="flex relative">
                                                                <span>
                                                                    <img
                                                                        src="assets/error-icon.svg"
                                                                        className={clsx(
                                                                            empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["otherIncomeType-" + income.displayId] != undefined
                                                                                ? ""
                                                                                : "hidden",
                                                                            "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                                        )}
                                                                        alt="error"
                                                                        height={5}
                                                                        width={5}
                                                                    />
                                                                </span>
                                                                <select
                                                                    id={"otherIncome" + income.displayId}
                                                                    value={income.type}
                                                                    onChange={(e) => {
                                                                        setOtherIncomeType(position.displayId, income.displayId, e.target.value)
                                                                    }}
                                                                    className={clsx(
                                                                        empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["otherIncomeType-" + income.displayId] != undefined
                                                                            ? "error"
                                                                            : "",
                                                                        "mt-2 w:[220px] md:w-[305px]"
                                                                    )}
                                                                    style={{ alignItems: 'center' }}
                                                                >
                                                                    <option hidden value="">
                                                                        Choose...
                                                                    </option>
                                                                    <option value="cpp">CPP</option>
                                                                    <option value="oas">OAS</option>
                                                                    <option value="employerpension">Employer Pension</option>
                                                                    <option value="permanentdisability">Permanent Disability</option>
                                                                    <option value="rrif">RRIF</option>
                                                                </select>
                                                            </div>
                                                            {empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["otherIncomeType-" + income.displayId] != undefined && (
                                                                <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                                    {empErrors[position.displayId - 1]["otherIncomeType-" + income.displayId]}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="ml-2">
                                                            <label htmlFor={income.displayId + "otherIncomeAmount"}>Income</label>
                                                            <div className="flex relative">
                                                                <span>
                                                                    <img
                                                                        src="assets/error-icon.svg"
                                                                        className={clsx(
                                                                            empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["otherIncomeAmount-" + income.displayId] != undefined
                                                                                ? ""
                                                                                : "hidden",
                                                                            "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[140px]"
                                                                        )}
                                                                        alt="error"
                                                                        height={5}
                                                                        width={5}
                                                                    />
                                                                </span>
                                                                <CurrencyInput
                                                                    id={income.displayId + "otherIncomeAmount"}
                                                                    placeholder="$"
                                                                    prefix={"$"}
                                                                    decimalsLimit={2}
                                                                    decimalScale={2}
                                                                    allowNegativeValue={false}
                                                                    maxLength={9}
                                                                    value={income.amountText}
                                                                    className={clsx(
                                                                        empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["otherIncomeAmount-" + income.displayId] != undefined
                                                                            ? "error"
                                                                            : "",
                                                                        "mt-2 w:[220px] md:w-[305px]"
                                                                    )}
                                                                    onValueChange={(value, name, values) => value != undefined ? setOtherIncomeAmount(position.displayId, income.displayId, value)
                                                                        : setOtherIncomeAmount(position.displayId, income.displayId, "")}
                                                                    style={{ maxWidth: 160 }}
                                                                />
                                                            </div>
                                                            {empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["otherIncomeAmount-" + income.displayId] != undefined && (
                                                                <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                                    {empErrors[position.displayId - 1]["otherIncomeAmount-" + income.displayId]}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="ml-3">
                                                            <label htmlFor={income.displayId + "otherIncomeFrequency"}>Frequency</label>
                                                            <div className="flex relative">
                                                                <span>
                                                                    <img
                                                                        src="assets/error-icon.svg"
                                                                        className={clsx(
                                                                            empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["otherIncomeFrequency-" + income.displayId] != undefined
                                                                                ? ""
                                                                                : "hidden",
                                                                            "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[120px]"
                                                                        )}
                                                                        alt="error"
                                                                        height={5}
                                                                        width={5}
                                                                    />
                                                                </span>
                                                                <select
                                                                    id={income.displayId + "otherIncomeFrequency"}
                                                                    value={income.frequency}
                                                                    onChange={(e) => {
                                                                        setOtherIncomeFrequency(position.displayId, income.displayId, e.target.value)
                                                                    }}
                                                                    className={clsx(
                                                                        empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["otherIncomeFrequency-" + income.displayId] != undefined
                                                                            ? "error"
                                                                            : "",
                                                                        "mt-2 w:[220px] md:w-[222px]"
                                                                    )}
                                                                    style={{ alignItems: 'center', maxWidth: 160 }}
                                                                >
                                                                    <option hidden value="">
                                                                        Choose...
                                                                    </option>
                                                                    <option value="weekly">Weekly</option>
                                                                    <option value="biweekly">Bi-Weekly</option>
                                                                    <option value="monthly">Monthly</option>
                                                                    <option value="semimonthly">Semi-Monthly</option>
                                                                    <option value="annually">Annually</option>
                                                                </select>
                                                            </div>
                                                            {empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["otherIncomeFrequency-" + income.displayId] != undefined && (
                                                                <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                                    {empErrors[position.displayId - 1]["otherIncomeFrequency-" + income.displayId]}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {income?.displayId === "1" && (
                                                            <div style={{ width: 80 }}></div>
                                                        )}
                                                        {income?.displayId !== "1" && (
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    removeOtherIncome(position.displayId, income.displayId);
                                                                }}
                                                                className="secondary sm:w-28 h-11 px-6 ml-3"
                                                                style={{ color: 'var(--brand-blue)', background: "transparent", width: 75, order: 0 }}>
                                                                <img
                                                                    className="mr-3"
                                                                    src="assets/remove-item.svg"
                                                                    alt="tooltip"
                                                                    width={20}
                                                                    height={20}
                                                                />
                                                            </button>
                                                        )}
                                                    </div>
                                                ))}
                                                <div className="mb-5">
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            addOtherIncome(position.displayId);
                                                        }}
                                                        className="secondary mt-5 w-full sm:w-28 h-11 px-6"
                                                        style={{ color: 'var(--brand-blue)', width: 330 }}>
                                                        <img
                                                            className="mr-3"
                                                            style={{ marginBottom: 2 }}
                                                            src="assets/add-circle.svg"
                                                            alt="tooltip"
                                                            width={15}
                                                            height={15}
                                                        />Add Additional Retirement Income
                                                    </button>
                                                </div>
                                            </>
                                        }


                                        {/* Additional Income from Employment */}
                                        {(position?.status !== "retired") &&
                                            <>
                                                <div className="mt-5">
                                                    <label htmlFor="">Do you receive additional income from this employer?</label>
                                                    <p></p>
                                                    <small className="text-gray-400">
                                                        Select all that apply. We will require you to provide 2 years history as proof when we review your application.
                                                    </small>

                                                </div>
                                                <div className="flex flex-row flex-wrap">
                                                    <div className="mt-5 flex flex-col-reverse">
                                                        <label></label>
                                                        <input
                                                            id={position.displayId + "overtimeprompt"}
                                                            type="checkbox"
                                                            checked={position.hasOvertimeIncome}
                                                            onChange={(e) => setOvertimeSelected(position.displayId, e.target.checked)}
                                                            className="accent"
                                                        />
                                                        <label htmlFor="overtimeprompt">
                                                            <p className="sub-heading ml-7 -mt-5">
                                                                Overtime
                                                            </p>
                                                        </label>
                                                    </div>
                                                    <div className="mt-5 ml-4 flex flex-col-reverse">
                                                        <label></label>
                                                        <input
                                                            id={position.displayId + "bonusprompt"}
                                                            type="checkbox"
                                                            checked={position.hasBonusIncome}
                                                            onChange={(e) => setBonusSelected(position.displayId, e.target.checked)}
                                                            className="accent"
                                                        />
                                                        <label htmlFor="bonusprompt">
                                                            <p className="sub-heading ml-7 -mt-5">
                                                                Bonus
                                                            </p>
                                                        </label>
                                                    </div>
                                                    <div className="mt-5 ml-4 flex flex-col-reverse">
                                                        <label></label>
                                                        <input
                                                            id={position.displayId + "commissionprompt"}
                                                            type="checkbox"
                                                            checked={position.hasCommissionIncome}
                                                            onChange={(e) => setCommissionSelected(position.displayId, e.target.checked)}
                                                            className="accent"
                                                        />
                                                        <label htmlFor="commissionprompt">
                                                            <p className="sub-heading ml-7 -mt-5">
                                                                Commission
                                                            </p>
                                                        </label>
                                                    </div>
                                                    <div className="mt-5 ml-4 flex flex-col-reverse">
                                                        <label></label>
                                                        <input
                                                            id="tipsprompt"
                                                            type="checkbox"
                                                            checked={position.hasTipsIncome}
                                                            onChange={(e) => setTipsSelected(position.displayId, e.target.checked)}
                                                            className="accent"
                                                        />
                                                        <label htmlFor="tipsprompt">
                                                            <p className="sub-heading ml-7 -mt-5">
                                                                Declared Tips
                                                            </p>
                                                        </label>
                                                    </div>
                                                </div>

                                                {position.hasOvertimeIncome &&
                                                    <div className="flex md:flex-row flex-col">
                                                        <div className={clsx(style.twoColFirst, "mt-5")}>
                                                            <label htmlFor={position.displayId + "overtimeIncome"}>Overtime</label>
                                                            <div className="flex relative">
                                                                <span>
                                                                    <img
                                                                        src="assets/error-icon.svg"
                                                                        className={clsx(
                                                                            empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["overtimeIncome"] != undefined
                                                                                ? ""
                                                                                : "hidden",
                                                                            "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                                        )}
                                                                        alt="error"
                                                                        height={5}
                                                                        width={5}
                                                                    />
                                                                </span>
                                                                <CurrencyInput
                                                                    id={position.displayId + "overtimeIncome"}
                                                                    placeholder="$"
                                                                    prefix={"$"}
                                                                    decimalsLimit={2}
                                                                    decimalScale={2}
                                                                    allowNegativeValue={false}
                                                                    maxLength={9}
                                                                    value={position.overtimeIncomeText}
                                                                    className={clsx(
                                                                        empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["overtimeIncome"] != undefined
                                                                            ? "error"
                                                                            : "",
                                                                        "mt-2 w:[220px] md:w-[305px]"
                                                                    )}
                                                                    onValueChange={(value, name, values) => value != undefined ? setOvertimeIncomeAmount(position.displayId, value) : setOvertimeIncomeAmount(position.displayId, "")}
                                                                />
                                                            </div>
                                                            {empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["overtimeIncome"] != undefined && (
                                                                <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                                    {empErrors[position.displayId - 1]["overtimeIncome"]}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="mt-5">
                                                            <label htmlFor="payrollFrequency">Frequency</label>
                                                            <div className="flex relative">
                                                                <span>
                                                                    <img
                                                                        src="assets/error-icon.svg"
                                                                        className={clsx(
                                                                            empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["overtimeIncomeFrequency"] != undefined
                                                                                ? "" : "hidden",
                                                                            "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                                        )}
                                                                        alt="error"
                                                                        height={5}
                                                                        width={5}
                                                                    />
                                                                </span>
                                                                <select
                                                                    id="overtimeIncomeFrequency"
                                                                    value={position.overtimeIncomeFrequency}
                                                                    onChange={(e) => {
                                                                        setOvertimeIncomeFrequency(position.displayId, e.target.value)
                                                                    }}
                                                                    className={clsx(
                                                                        empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["overtimeIncomeFrequency"] != undefined
                                                                            ? "error"
                                                                            : "",
                                                                        "mt-2 w:[200px] md:w-[305px]"
                                                                    )}
                                                                    style={{ alignItems: 'center' }}
                                                                >
                                                                    <option hidden value="">
                                                                        Choose...
                                                                    </option>
                                                                    <option value="weekly">Weekly</option>
                                                                    <option value="biweekly">Bi-Weekly</option>
                                                                    <option value="monthly">Monthly</option>
                                                                    <option value="semimonthly">Semi-Monthly</option>
                                                                    <option value="annually">Annually</option>
                                                                </select>
                                                            </div>
                                                            {empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["overtimeIncomeFrequency"] != undefined && (
                                                                <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                                    {empErrors[position.displayId - 1]["overtimeIncomeFrequency"]}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                }

                                                {position.hasBonusIncome &&
                                                    <div className="flex md:flex-row flex-col">
                                                        <div className={clsx(style.twoColFirst, "mt-5")}>
                                                            <label htmlFor={position.displayId + "bonusIncome"}>Bonus</label>
                                                            <div className="flex relative">
                                                                <span>
                                                                    <img
                                                                        src="assets/error-icon.svg"
                                                                        className={clsx(
                                                                            empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["bonusIncome"] != undefined
                                                                                ? ""
                                                                                : "hidden",
                                                                            "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                                        )}
                                                                        alt="error"
                                                                        height={5}
                                                                        width={5}
                                                                    />
                                                                </span>
                                                                <CurrencyInput
                                                                    id={position.displayId + "bonusIncome"}
                                                                    placeholder="$"
                                                                    prefix={"$"}
                                                                    decimalsLimit={2}
                                                                    decimalScale={2}
                                                                    allowNegativeValue={false}
                                                                    maxLength={9}
                                                                    value={position.bonusIncomeText}
                                                                    className={clsx(
                                                                        empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["bonusIncome"] != undefined
                                                                            ? "error"
                                                                            : "",
                                                                        "mt-2 w:[220px] md:w-[305px]"
                                                                    )}
                                                                    onValueChange={(value, name, values) => value != undefined ? setBonusIncomeAmount(position.displayId, value) : setBonusIncomeAmount(position.displayId, "")}
                                                                />
                                                            </div>
                                                            {empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["bonusIncome"] != undefined && (
                                                                <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                                    {empErrors[position.displayId - 1]["bonusIncome"]}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="mt-5">
                                                            <label htmlFor="bonusIncomeFrequency">Frequency</label>
                                                            <div className="flex relative">
                                                                <span>
                                                                    <img
                                                                        src="assets/error-icon.svg"
                                                                        className={clsx(
                                                                            empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["bonusIncomeFrequency"] != undefined
                                                                                ? "" : "hidden",
                                                                            "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                                        )}
                                                                        alt="error"
                                                                        height={5}
                                                                        width={5}
                                                                    />
                                                                </span>
                                                                <select
                                                                    id="bonusIncomeFrequency"
                                                                    value={position.bonusIncomeFrequency}
                                                                    onChange={(e) => {
                                                                        setBonusIncomeFrequency(position.displayId, e.target.value)
                                                                    }}
                                                                    className={clsx(
                                                                        empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["bonusIncomeFrequency"] != undefined
                                                                            ? "error"
                                                                            : "",
                                                                        "mt-2 w:[220px] md:w-[305px]"
                                                                    )}
                                                                    style={{ alignItems: 'center' }}
                                                                >
                                                                    <option hidden value="">
                                                                        Choose...
                                                                    </option>
                                                                    <option value="weekly">Weekly</option>
                                                                    <option value="biweekly">Bi-Weekly</option>
                                                                    <option value="monthly">Monthly</option>
                                                                    <option value="semimonthly">Semi-Monthly</option>
                                                                    <option value="annually">Annually</option>
                                                                </select>
                                                            </div>
                                                            {empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["bonusIncomeFrequency"] != undefined && (
                                                                <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                                    {empErrors[position.displayId - 1]["bonusIncomeFrequency"]}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                }

                                                {position.hasCommissionIncome &&
                                                    <div className="flex md:flex-row flex-col">
                                                        <div className={clsx(style.twoColFirst, "mt-5")}>
                                                            <label htmlFor={position.displayId + "commissionIncome"}>Commission</label>
                                                            <div className="flex relative">
                                                                <span>
                                                                    <img
                                                                        src="assets/error-icon.svg"
                                                                        className={clsx(
                                                                            empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["commissionIncome"] != undefined
                                                                                ? ""
                                                                                : "hidden",
                                                                            "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                                        )}
                                                                        alt="error"
                                                                        height={5}
                                                                        width={5}
                                                                    />
                                                                </span>
                                                                <CurrencyInput
                                                                    id={position.displayId + "commissionIncome"}
                                                                    placeholder="$"
                                                                    prefix={"$"}
                                                                    decimalsLimit={2}
                                                                    decimalScale={2}
                                                                    allowNegativeValue={false}
                                                                    maxLength={9}
                                                                    value={position.commissionIncomeText}
                                                                    className={clsx(
                                                                        empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["commissionIncome"] != undefined
                                                                            ? "error"
                                                                            : "",
                                                                        "mt-2 w:[220px] md:w-[305px]"
                                                                    )}
                                                                    onValueChange={(value, name, values) => value != undefined ? setCommissionIncomeAmount(position.displayId, value) : setCommissionIncomeAmount(position.displayId, "")}
                                                                />
                                                            </div>
                                                            {empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["commissionIncome"] != undefined && (
                                                                <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                                    {empErrors[position.displayId - 1]["commissionIncome"]}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="mt-5">
                                                            <label htmlFor={position.displayId + "commissionIncomeFrequency"}>Frequency</label>
                                                            <div className="flex relative">
                                                                <span>
                                                                    <img
                                                                        src="assets/error-icon.svg"
                                                                        className={clsx(
                                                                            empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["commissionIncomeFrequency"] != undefined
                                                                                ? "" : "hidden",
                                                                            "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                                        )}
                                                                        alt="error"
                                                                        height={5}
                                                                        width={5}
                                                                    />
                                                                </span>
                                                                <select
                                                                    id={position.displayId + "commissionIncomeFrequency"}
                                                                    value={position.commissionIncomeFrequency}
                                                                    onChange={(e) => {
                                                                        setCommissionIncomeFrequency(position.displayId, e.target.value)
                                                                    }}
                                                                    className={clsx(
                                                                        empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["commissionIncomeFrequency"] != undefined
                                                                            ? "error"
                                                                            : "",
                                                                        "mt-2 w:[220px] md:w-[305px]"
                                                                    )}
                                                                    style={{ alignItems: 'center' }}
                                                                >
                                                                    <option hidden value="">
                                                                        Choose...
                                                                    </option>
                                                                    <option value="weekly">Weekly</option>
                                                                    <option value="biweekly">Bi-Weekly</option>
                                                                    <option value="monthly">Monthly</option>
                                                                    <option value="semimonthly">Semi-Monthly</option>
                                                                    <option value="annually">Annually</option>
                                                                </select>
                                                            </div>
                                                            {empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["commissionIncomeFrequency"] != undefined && (
                                                                <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                                    {empErrors[position.displayId - 1]["commissionIncomeFrequency"]}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                }

                                                {position.hasTipsIncome &&
                                                    <div className="flex md:flex-row flex-col">
                                                        <div className={clsx(style.twoColFirst, "mt-5")}>
                                                            <label htmlFor={position.displayId + "tipsIncome"}>Declared Tips</label>
                                                            <div className="flex relative">
                                                                <span>
                                                                    <img
                                                                        src="assets/error-icon.svg"
                                                                        className={clsx(
                                                                            empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["tipsIncome"] != undefined
                                                                                ? ""
                                                                                : "hidden",
                                                                            "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                                        )}
                                                                        alt="error"
                                                                        height={5}
                                                                        width={5}
                                                                    />
                                                                </span>
                                                                <CurrencyInput
                                                                    id={position.displayId + "tipsIncome"}
                                                                    placeholder="$"
                                                                    prefix={"$"}
                                                                    decimalsLimit={2}
                                                                    decimalScale={2}
                                                                    allowNegativeValue={false}
                                                                    maxLength={9}
                                                                    value={position.tipsIncomeText}
                                                                    className={clsx(
                                                                        empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["tipsIncome"] != undefined
                                                                            ? "error"
                                                                            : "",
                                                                        "mt-2 w:[220px] md:w-[305px]"
                                                                    )}
                                                                    onValueChange={(value, name, values) => value != undefined ? setTipsIncomeAmount(position.displayId, value) : setTipsIncomeAmount(position.displayId, "")}
                                                                />
                                                            </div>
                                                            {empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["tipsIncome"] != undefined && (
                                                                <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                                    {empErrors[position.displayId - 1]["tipsIncome"]}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="mt-5">
                                                            <label htmlFor={position.displayId + "tipsIncomeFrequency"}>Frequency</label>
                                                            <div className="flex relative">
                                                                <span>
                                                                    <img
                                                                        src="assets/error-icon.svg"
                                                                        className={clsx(
                                                                            empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["tipsIncomeFrequency"] != undefined
                                                                                ? "" : "hidden",
                                                                            "w-4 h-4 absolute top-5 max-xxs:ml-[40px] max-sm:ml-[70px] xs:max-md:ml-[80px] md:ml-[110px]"
                                                                        )}
                                                                        alt="error"
                                                                        height={5}
                                                                        width={5}
                                                                    />
                                                                </span>
                                                                <select
                                                                    id={position.displayId + "tipsIncomeFrequency"}
                                                                    value={position.tipsIncomeFrequency}
                                                                    onChange={(e) => {
                                                                        setTipsIncomeFrequency(position.displayId, e.target.value)
                                                                    }}
                                                                    className={clsx(
                                                                        empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["tipsIncomeFrequency"] != undefined
                                                                            ? "error"
                                                                            : "",
                                                                        "mt-2 w:[220px] md:w-[305px]"
                                                                    )}
                                                                    style={{ alignItems: 'center' }}
                                                                >
                                                                    <option hidden value="">
                                                                        Choose...
                                                                    </option>
                                                                    <option value="weekly">Weekly</option>
                                                                    <option value="biweekly">Bi-Weekly</option>
                                                                    <option value="monthly">Monthly</option>
                                                                    <option value="semimonthly">Semi-Monthly</option>
                                                                    <option value="annually">Annually</option>
                                                                </select>
                                                            </div>
                                                            {empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["tipsIncomeFrequency"] != undefined && (
                                                                <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                                    {empErrors[position.displayId - 1]["tipsIncomeFrequency"]}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                }


                                                {/* Employer Name & Phone Number*/}

                                                <hr className="mt-5" />

                                                <div className="flex md:flex-row flex-col">
                                                    <div className={clsx(style.twoColFirst, "mt-5")}>
                                                        <label htmlFor={position.displayId + "employerName"}>Employer name</label>
                                                        <div className="flex relative">
                                                            <span>
                                                                <img
                                                                    src="assets/error-icon.svg"
                                                                    className={clsx(
                                                                        empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["employerName"] != undefined
                                                                            ? ""
                                                                            : "hidden",
                                                                        "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                                    )}
                                                                    alt="error"
                                                                    height={5}
                                                                    width={5}
                                                                />
                                                            </span>
                                                            <input
                                                                className={clsx(
                                                                    empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["employerName"] != undefined
                                                                        ? "error"
                                                                        : "",
                                                                    "mt-2 w:[220px] md:w-[305px]"
                                                                )}
                                                                type="text"
                                                                id={position.displayId + "employerName"}
                                                                onChange={(e) => {
                                                                    setEmployerName(position.displayId, e.target.value)
                                                                }}
                                                                value={position.employerName}
                                                                maxLength={30}
                                                            />
                                                        </div>
                                                        {empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["employerName"] != undefined && (
                                                            <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                                {empErrors[position.displayId - 1]["employerName"]}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="mt-5">
                                                        <label htmlFor="employerPhoneNumber">Employer phone number</label>
                                                        <div className="flex relative">
                                                            <span>
                                                                <img
                                                                    src="assets/error-icon.svg"
                                                                    className={clsx(
                                                                        empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["employerPhoneNumber"] != undefined
                                                                            ? ""
                                                                            : "hidden",
                                                                        "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                                    )}
                                                                    alt="error"
                                                                    height={5}
                                                                    width={5}
                                                                />
                                                            </span>
                                                            <InputMask
                                                                type="tel"
                                                                id={"employerPhone" + position.displayId}
                                                                placeholder="(000) 000-0000"
                                                                alwaysShowMask={false}
                                                                maskChar={null}
                                                                value={position.employerPhone}
                                                                mask="(999) 999-9999"
                                                                className={clsx(
                                                                    empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["employerPhoneNumber"] != undefined
                                                                        ? "error"
                                                                        : "",
                                                                    "mt-2 w:[200px] md:w-[305px]"
                                                                )}
                                                                onChange={(e) => {
                                                                    setEmployerPhone(position.displayId, e.target.value)
                                                                }}
                                                            />
                                                        </div>
                                                        {empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["employerPhoneNumber"] != undefined && (
                                                            <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                                {empErrors[position.displayId - 1]["employerPhoneNumber"]}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Employer Address */}

                                                <div className="col-span-1 mt-5">
                                                    <div className={""}>
                                                        <label htmlFor="search">Employer address</label>
                                                        <div className="flex md:flex-row flex-col justify-between">
                                                            <div className="flex relative">
                                                                <span>
                                                                    <img
                                                                        src="assets/magnifying-glass-icon.svg"
                                                                        className={clsx(
                                                                            methods.formState?.errors?.autoAddress || (empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["autoAddress"] != undefined)
                                                                                ? "hidden"
                                                                                : "",
                                                                            "w-5 h-5 absolute top-5 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                                                                        )}
                                                                        alt="search"
                                                                        height={5}
                                                                        width={5}
                                                                    />
                                                                    <img
                                                                        src="assets/error-icon.svg"
                                                                        className={clsx(
                                                                            methods.formState?.errors?.autoAddress || (empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["autoAddress"] != undefined)
                                                                                ? ""
                                                                                : "hidden",
                                                                            "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                                                                        )}
                                                                        alt="error"
                                                                        height={5}
                                                                        width={5}
                                                                    />
                                                                </span>
                                                                <input
                                                                    autoComplete="nope"
                                                                    className={clsx(
                                                                        methods.formState?.errors?.autoAddress?.message || (empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["autoAddress"] != undefined)
                                                                            ? "error"
                                                                            : "",
                                                                        "mt-2"
                                                                    )}
                                                                    id={"empAddressSearch" + position.displayId}
                                                                    type="text"
                                                                    placeholder="Start typing address"
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className="flex relative mt-2">
                                                            <p className="active underline cursor-pointer" onClick={() => toggleManualAddress(position.displayId)}>Enter address manually</p>
                                                        </div>

                                                        {addressSelectionError && (
                                                            <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                                You must select an address from the list of options
                                                            </span>
                                                        )}
                                                        <div className={clsx(position.employerAddress.city != "" ? "" : "hidden")}>
                                                            <p id={"formatted-address" + position.displayId}></p>
                                                            {position.employerAddress.unitNumber != "" && <><span >{position.employerAddress.unitNumber}-</span></>}
                                                            {position.employerAddress.streetNumber != "" && <><span >{position.employerAddress.streetNumber} {position.employerAddress.streetName} {position.employerAddress.streetType}
                                                                {position.employerAddress.streetDirection != "" && " " + position.employerAddress.streetDirection}</span><br /></>}
                                                            {position.employerAddress.city != "" && <><span >{position.employerAddress.city}</span> &nbsp;</>}
                                                            {position.employerAddress.province != "" && <><span >{position.employerAddress.province}</span>&nbsp;</>}
                                                            {position.employerAddress.postalCode != "" && <><span >{position.employerAddress.postalCode}</span><br /></>}
                                                            {position.employerAddress.country != "" && <span >{position.employerAddress.country}</span>}
                                                        </div>
                                                        <div
                                                            className={clsx(
                                                                position.manualAddressEntered ? "" : "hidden",
                                                                "flex relative"
                                                            )}
                                                        >
                                                            <div className="mt-5">
                                                                <ManualAddress passAddressData={getAddressData} addressData={position.employerAddress} addressErrors={empAddrErrors} errorIndex={position.displayId - 1} />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>


                                                {/* Miscellaneous Employment Info */}

                                                <hr className="mt-5" />

                                                <div className="flex md:flex-row flex-col">
                                                    <div className={clsx("mt-5", style.twoColFirst)}>
                                                        <label>Are you currently on probation?</label>
                                                        <div className="grid grid-flow-row sm:grid-flow-col">
                                                            <ul className="grid grid-cols-3 gap-x-2 mt-2 max-w-md" style={{ maxWidth: "20rem" }}>
                                                                <li className="relative">
                                                                    <input
                                                                        type="radio"
                                                                        value="yes"
                                                                        id={position.displayId + "yes-probation"}
                                                                        checked={position.isProbation === "yes"}
                                                                        onChange={(e) => {
                                                                            setProbationExists(position.displayId, e.target.value)
                                                                        }}
                                                                        className="sr-only peer "
                                                                    />
                                                                    <label
                                                                        className={clsx(
                                                                            style.phoneLabel,
                                                                            "cursor-pointer focus:outline-none hover:bg-gray-100 peer-checked:ring-[--brand-blue] peer-checked:ring-2 peer-checked:border-transparent text-sm text-center",
                                                                            empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["probation"] != undefined
                                                                                ? style.labelError
                                                                                : ""
                                                                        )}
                                                                        htmlFor={position.displayId + "yes-probation"}
                                                                    >
                                                                        <p className="p-2 brand-blue-text leading-6" style={{ lineHeight: "1.5rem" }}>Yes</p>
                                                                    </label>
                                                                </li>
                                                                <li className="relative">
                                                                    <input
                                                                        type="radio"
                                                                        value="no"
                                                                        id={position.displayId + "no-probation"}
                                                                        checked={position.isProbation === "no"}
                                                                        onChange={(e) => {
                                                                            setProbationExists(position.displayId, e.target.value)
                                                                        }}
                                                                        className="sr-only peer leading-6"
                                                                    />
                                                                    <label
                                                                        className={clsx(
                                                                            style.phoneLabel,
                                                                            "cursor-pointer focus:outline-none hover:bg-gray-100 peer-checked:ring-[--brand-blue] peer-checked:ring-2 peer-checked:border-transparent text-sm text-center",
                                                                            empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["probation"] != undefined
                                                                                ? style.labelError
                                                                                : ""
                                                                        )}
                                                                        htmlFor={position.displayId + "no-probation"}
                                                                    >
                                                                        <p className="p-2 brand-blue-text leading-6" style={{ lineHeight: "1.5rem" }}>No</p>
                                                                    </label>
                                                                </li>
                                                            </ul>
                                                        </div>
                                                        {empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["isProbation"] != undefined && (
                                                            <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                                {empErrors[position.displayId - 1]["isProbation"]}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className={clsx("mt-5", style.twoColFirst)}>
                                                        <label>Is this business owned by a family member?</label>
                                                        <div className="grid grid-flow-row sm:grid-flow-col">
                                                            <ul className="grid grid-cols-3 gap-x-2 mt-2 max-w-md" style={{ maxWidth: "20rem" }}>
                                                                <li className="relative">
                                                                    <input
                                                                        type="radio"
                                                                        value="yes"
                                                                        id={position.displayId + "yes-business-family-owned"}
                                                                        checked={position.isFamilyOwned === "yes"}
                                                                        onChange={(e) => {
                                                                            setFamilyOwned(position.displayId, e.target.value)
                                                                        }}
                                                                        className="sr-only peer "
                                                                    />
                                                                    <label
                                                                        className={clsx(
                                                                            style.phoneLabel,
                                                                            "cursor-pointer focus:outline-none hover:bg-gray-100 peer-checked:ring-[--brand-blue] peer-checked:ring-2 peer-checked:border-transparent text-sm text-center",
                                                                            empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["businessFamilyOwned"] != undefined
                                                                                ? style.labelError
                                                                                : ""
                                                                        )}
                                                                        htmlFor={position.displayId + "yes-business-family-owned"}
                                                                    >
                                                                        <p className="p-2 brand-blue-text leading-6" style={{ lineHeight: "1.5rem" }}>Yes</p>
                                                                    </label>
                                                                </li>
                                                                <li className="relative">
                                                                    <input
                                                                        type="radio"
                                                                        value="no"
                                                                        id={position.displayId + "no-business-family-owned"}
                                                                        checked={position.isFamilyOwned === "no"}
                                                                        onChange={(e) => {
                                                                            setFamilyOwned(position.displayId, e.target.value)
                                                                        }}
                                                                        className="sr-only peer leading-6"
                                                                    />
                                                                    <label
                                                                        className={clsx(
                                                                            style.phoneLabel,
                                                                            "cursor-pointer focus:outline-none hover:bg-gray-100 peer-checked:ring-[--brand-blue] peer-checked:ring-2 peer-checked:border-transparent text-sm text-center",
                                                                            empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["businessFamilyOwned"] != undefined
                                                                                ? style.labelError
                                                                                : ""
                                                                        )}
                                                                        htmlFor={position.displayId + "no-business-family-owned"}
                                                                    >
                                                                        <p className="p-2 brand-blue-text leading-6" style={{ lineHeight: "1.5rem" }}>No</p>
                                                                    </label>
                                                                </li>
                                                            </ul>
                                                        </div>
                                                        {empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["isFamilyOwned"] != undefined && (
                                                            <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                                {empErrors[position.displayId - 1]["isFamilyOwned"]}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="flex md:flex-row flex-col mt-5">
                                                    <div className={style.twoColFirst}>
                                                        <label>Are you currently on leave?</label>
                                                        <div className="grid grid-flow-row sm:grid-flow-col">
                                                            <ul className="grid grid-cols-3 gap-x-2 mt-2 max-w-md" style={{ maxWidth: "20rem" }}>
                                                                <li className="relative">
                                                                    <input
                                                                        type="radio"
                                                                        value="yes"
                                                                        id={position.displayId + "yes-leave"}
                                                                        checked={position.isOnLeave === "yes"}
                                                                        onChange={(e) => {
                                                                            setOnLeave(position.displayId, e.target.value)
                                                                        }}
                                                                        className="sr-only peer "
                                                                    />
                                                                    <label
                                                                        className={clsx(
                                                                            style.phoneLabel,
                                                                            "cursor-pointer focus:outline-none hover:bg-gray-100 peer-checked:ring-[--brand-blue] peer-checked:ring-2 peer-checked:border-transparent text-sm text-center",
                                                                            empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["leave"] != undefined
                                                                                ? style.labelError
                                                                                : ""
                                                                        )}
                                                                        htmlFor={position.displayId + "yes-leave"}
                                                                    >
                                                                        <p className="p-2 brand-blue-text leading-6" style={{ lineHeight: "1.5rem" }}>Yes</p>
                                                                    </label>
                                                                </li>
                                                                <li className="relative">
                                                                    <input
                                                                        type="radio"
                                                                        value="no"
                                                                        id={position.displayId + "no-leave"}
                                                                        checked={position.isOnLeave === "no"}
                                                                        onChange={(e) => {
                                                                            setOnLeave(position.displayId, e.target.value)
                                                                        }}
                                                                        className="sr-only peer leading-6"
                                                                    />
                                                                    <label
                                                                        className={clsx(
                                                                            style.phoneLabel,
                                                                            "cursor-pointer focus:outline-none hover:bg-gray-100 peer-checked:ring-[--brand-blue] peer-checked:ring-2 peer-checked:border-transparent text-sm text-center",
                                                                            empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["leave"] != undefined
                                                                                ? style.labelError
                                                                                : ""
                                                                        )}
                                                                        htmlFor={position.displayId + "no-leave"}
                                                                    >
                                                                        <p className="p-2 brand-blue-text leading-6" style={{ lineHeight: "1.5rem" }}>No</p>
                                                                    </label>
                                                                </li>
                                                            </ul>
                                                        </div>
                                                        {empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["isOnLeave"] != undefined && (
                                                            <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                                {empErrors[position.displayId - 1]["isOnLeave"]}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>

                                                {position.isOnLeave === "yes" &&
                                                    <div style={{ borderLeft: "thick solid #D7DEE0" }} className="mt-5">
                                                        <label className="ml-3">Type of Leave: </label>
                                                        <div className="grid grid-flow-row sm:grid-flow-col">
                                                            <ul className="flex flex-row gap-x-2 max-w-md" style={{ maxWidth: "20rem" }}>
                                                                <li className="flex flex-row">
                                                                    <label
                                                                        className={clsx(
                                                                            "flex flex-row-reverse",
                                                                            empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["leaveType"] != undefined
                                                                                ? style.labelError
                                                                                : ""
                                                                        )}
                                                                        htmlFor={position.displayId + "parental-leave"}
                                                                    >
                                                                        <p className="p-2 leading-6" style={{ whiteSpace: "nowrap" }}>Parental Leave</p>

                                                                        <input
                                                                            type="radio"
                                                                            value="parental"
                                                                            id={position.displayId + "parental-leave"}
                                                                            checked={position.leaveType === "parental"}
                                                                            onChange={(e) => {
                                                                                setLeaveType(position.displayId, e.target.value)
                                                                            }}
                                                                            className="peer"
                                                                            style={{ width: 50, height: 18, marginTop: 14, accentColor: "var(--brand-blue)" }}
                                                                        />
                                                                    </label>


                                                                </li>
                                                                <li className="flex flex-row">
                                                                    <label
                                                                        className={clsx(
                                                                            "flex flex-row-reverse",
                                                                            empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["leaveType"] != undefined
                                                                                ? style.labelError
                                                                                : ""
                                                                        )}
                                                                        htmlFor={position.displayId + "other-leave"}
                                                                    >
                                                                        <p className="p-2 leading-6" style={{ whiteSpace: "nowrap" }}>Other Leave</p>

                                                                        <input
                                                                            type="radio"
                                                                            value="other"
                                                                            id={position.displayId + "other-leave"}
                                                                            onChange={(e) => {
                                                                                setLeaveType(position.displayId, e.target.value)
                                                                            }}
                                                                            checked={position.leaveType === "other"}
                                                                            className="peer leading-6"
                                                                            style={{ width: 50, height: 18, marginTop: 14, accentColor: "var(--brand-blue)" }}
                                                                        />
                                                                    </label>
                                                                </li>
                                                            </ul>
                                                        </div>
                                                        {empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["leaveType"] != undefined && (
                                                            <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-4">
                                                                {empErrors[position.displayId - 1]["leaveType"]}
                                                            </span>
                                                        )}
                                                        {position.leaveType === "other" &&
                                                            <div className={clsx(style.twoColFirst, "ml-3 mt-3")}>
                                                                <label htmlFor={position.display + "leaveDesc"}>Description of Leave</label>
                                                                <div className="flex relative">
                                                                    <span>
                                                                        <img
                                                                            src="assets/error-icon.svg"
                                                                            className={clsx(
                                                                                empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["otherLeaveDesc"] != undefined
                                                                                    ? "" : "hidden",
                                                                                "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                                            )}
                                                                            alt="error"
                                                                            height={5}
                                                                            width={5}
                                                                        />
                                                                    </span>
                                                                    <input
                                                                        className={clsx(
                                                                            empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["otherLeaveDesc"] != undefined
                                                                                ? "error"
                                                                                : "",
                                                                            "mt-2 w:[220px] md:w-[305px]"
                                                                        )}
                                                                        type="text"
                                                                        id={position.display + "leaveDesc"}
                                                                        onChange={(e) => {
                                                                            setLeaveDescription(position.displayId, e.target.value)
                                                                        }}
                                                                        value={position.otherLeaveDescription}
                                                                    />
                                                                </div>
                                                                {empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["otherLeaveDesc"] != undefined && (
                                                                    <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                                        {empErrors[position.displayId - 1]["otherLeaveDesc"]}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        }

                                                        <div className="ml-4 mt-3">
                                                            <label htmlFor="downPayment">Return to work date</label>
                                                            <div className="flex relative">
                                                                <span>
                                                                    <img
                                                                        src="assets/error-icon.svg"
                                                                        className={clsx(
                                                                            empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["workReturnDate"] != undefined
                                                                                ? ""
                                                                                : "hidden",
                                                                            "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[195px]"
                                                                        )}
                                                                        alt="error"
                                                                        height={5}
                                                                        width={5}
                                                                    />
                                                                </span>

                                                                <div>
                                                                    <DatePicker
                                                                        className={style.datePickerInput}
                                                                        format="DD MMM YYYY"
                                                                        minDate={dayjs().add(1, 'day')}
                                                                        value={dayjs(position.workReturnDate, 'DD/MM/YYYY')}
                                                                        onChange={(e) => {
                                                                            if (e != undefined) {
                                                                                methods.setValue("workReturnDate", e.format('DD/MM/YYYY'));
                                                                                setWorkReturnDate(position.displayId, e.format('DD/MM/YYYY'));
                                                                            }
                                                                        }}
                                                                        sx={{ width: window.innerWidth < 768 ? 350 : "100%" }}
                                                                        slotProps={{
                                                                            textField: {
                                                                                error: empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["workReturnDate"] != undefined,
                                                                            },
                                                                        }}
                                                                        localeText={{
                                                                            fieldMonthPlaceholder: (params) => (params.contentType === 'letter' ? 'MMM' : 'MM'),
                                                                        }}
                                                                    />
                                                                </div>

                                                            </div>
                                                            {empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["workReturnDate"] != undefined && (
                                                                <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                                    {empErrors[position.displayId - 1]["workReturnDate"]}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                }
                                            </>}
                                    </>
                                }
                            </>
                        }
                    </motion.div>
                ))}
            </AnimatePresence>
        )
    };

    const goBack = () => {
        if (editMode) {
            setFetchingCachedData(true);
            dispatch(clearCurrentEmploymentState());
            sessionData.cachedCurrentEmployment.forEach((employment) => {
                dispatch(addCurrentEmployment(employment));
            });
        }
        navigate(-1);
    }

    return (
        <>
            <SlideTransition>
                <div className="flex flex-col max-xs:px-3 sm:ml-80 max-md:items-center max-md:mt-20">
                    <div
                        className={style.addHeading}
                        role="region"
                        aria-label="Address Header"
                    >
                        <div className="max-md:ml-2">
                            <h1 aria-label="Address Header">What is your current work situation?</h1>
                            <p className="sub-heading">Tell us a bit more about your current employer. The information provided will be verified when we review your application.</p>
                        </div>
                        <div className={"flex flex-row"} style={{ maxWidth: "80%" }}>
                            <FormProvider {...methods}>
                                <form
                                    autoComplete="off"
                                    onSubmit={methods.handleSubmit(onSubmit, onError)}
                                    style={{ width: '-webkit-fill-available', paddingRight: '10%' }}
                                >
                                    {displayError &&
                                        <div className={clsx("flex flex-row pcaautocomplete mt-5", style.errorContainer)}>
                                            <img src="assets/error-info-icon.svg" alt="error" />
                                            <span className="text-sm ml-2 mt-1">
                                                {errorMessage}
                                            </span>
                                        </div>
                                    }
                                    <ConfirmDialog />
                                    {currentEmployment != undefined && currentEmployment.length > 0 && !fetchingCachedData &&
                                        <>
                                            {currentEmploymentList()}
                                        </>
                                    }

                                    <div className="mb-5">
                                        <button
                                            type="button"
                                            onClick={addCurrEmployment}
                                            className="secondary mt-5 w-full sm:w-28 h-11 px-6"
                                            style={{ width: '-webkit-fill-available', color: 'var(--brand-blue)' }}>
                                            <img
                                                className="mr-3"
                                                style={{ marginBottom: 2 }}
                                                src="assets/add-circle.svg"
                                                alt="tooltip"
                                                width={15}
                                                height={15}
                                            />
                                            Add {currentEmployment && currentEmployment.length > 0 && !fetchingCachedData ? "another employer" : "current employment"}
                                        </button>
                                    </div>


                                    <div className="flex max-sm:hidden">
                                        <div className="mb-5 mr-5">
                                            <button onClick={() => goBack()}
                                                type="button"
                                                className="back mt-5 w-169 sm:w-28 h-11 px-6">
                                                {editMode ? "Cancel" : "Back"}
                                            </button>
                                        </div>
                                        <div className="mb-5">
                                            <button
                                                type="submit"
                                                className="primary mt-5 w-full sm:w-28 h-11 px-6"
                                                style={{ background: currentEmployment && currentEmployment.length === 0 ? "#00558052" : "var(--brand-blue)" }}
                                                disabled={currentEmployment.length === 0 || isSaving}
                                            >
                                                <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} className={clsx(isSaving ? "" : "hidden")} />
                                                {editMode ? "Save" : "Continue"}
                                            </button>
                                        </div>
                                    </div>
                                    <div className="sm:hidden">
                                        <div className="mb-5">
                                            <button
                                                type="submit"
                                                className="primary mt-5 w-full sm:w-28 h-11 px-6"
                                                disabled={currentEmployment.length === 0 || isSaving}
                                                style={{ background: currentEmployment && currentEmployment.length === 0 ? "#00558052" : "var(--brand-blue)" }}
                                            >
                                                <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} className={clsx(isSaving ? "" : "hidden")} />
                                                {editMode ? "Save" : "Continue"}
                                            </button>
                                        </div>
                                        <div className="mb-5">
                                            <button onClick={() => goBack()}
                                                type="button"
                                                className="back mt-5 w-full sm:w-28 h-11 px-6">
                                                {editMode ? "Cancel" : "Back"}
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </FormProvider>
                        </div>
                    </div>
                </div>
            </SlideTransition>
        </>
    );
}